import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  useMediaQuery,
  useTheme,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import BigBoard from './BigBoard';
import {
  Logout as LogoutIcon,
  Radio as RadioIcon,
  Warning as WarningIcon,
  AccountCircle as AccountIcon,
  Dashboard as BigBoardIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Map as MapIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import iconImage from '../assets/icon.png';
import { useAuth } from '../hooks/useAuth.tsx';
import { requestUnitUnclaim, sendUnitUnclaimBeacon } from '../utils/unitUnclaim';
import { usePermissions } from '../hooks/usePermissions.tsx';
import { useSocket } from '../contexts/SocketContext.tsx';
import { useDispatchMode } from '../hooks/useDispatchMode';
import { http } from '../lib/http';
import { useToast } from '../contexts/ToastContext.tsx';
import { useConfig } from '../contexts/ConfigContext.tsx';
import { audioNotifications } from '../utils/audioNotifications';
// import CallList from './CallList.tsx';
import CallDetails from './CallDetails.tsx';
import AdminInterface from './AdminInterface.tsx';
import ShiftCommanderInterface from './ShiftCommanderInterface.tsx';
import MapModal from './MapModal.tsx';
import { Unit, Call, UnitType } from '../types/index.ts';

interface UnitInterfaceProps {
  onReturnToShiftSelection?: () => void;
}

const UnitInterface: React.FC<UnitInterfaceProps> = ({ onReturnToShiftSelection }) => {
  const { logout, user } = useAuth();
  const { hasPermission } = usePermissions();
  const socket = useSocket();
  const { showInfo, showWarning, showSuccess, showError } = useToast();
  const { callCodes } = useConfig();
  const { isActiveDispatchMode } = useDispatchMode();
  
  // Mobile responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Account management state
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [logoutDialog, setLogoutDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showShiftCommander, setShowShiftCommander] = useState(false);
  const [showRoleSelectionDialog, setShowRoleSelectionDialog] = useState(false);
  const [selectedUnitForRole, setSelectedUnitForRole] = useState<Unit | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [showUnitRolesDialog, setShowUnitRolesDialog] = useState(false);
  const [selectedUnitForRoles, setSelectedUnitForRoles] = useState<Unit | null>(null);
  const [showLastUnitPrompt, setShowLastUnitPrompt] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ unitId: string; status: string; callId: string } | null>(null);
  
  // Mobile responsive state
  const [showMobileUnitsPanel, setShowMobileUnitsPanel] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);

  // Track if data loading is in progress to prevent multiple simultaneous calls
  const [isDataLoading, setIsDataLoading] = useState(false);
  
  // Track which units have claimers (maps unitId to array of claimers)
  const [unitClaims, setUnitClaims] = useState<Record<string, any[]>>({});
  
  // Track if we're logging out to prevent infinite loops
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Track user assignment (Active Dispatch mode)
  const [userAssignment, setUserAssignment] = useState<any>(null);
  
  // Icon state
  const [iconLoaded, setIconLoaded] = useState(false);
  
  // Refs to track intervals for cleanup
  const tokenValidationInterval = React.useRef<NodeJS.Timeout | null>(null);
  
  // State version tracking for monotonicity validation
  const [isReady, setIsReady] = useState<boolean>(false);
  const [lastStateUpdateTime, setLastStateUpdateTime] = useState<number | null>(null);
  const [stateRequestDebounce, setStateRequestDebounce] = useState<NodeJS.Timeout | null>(null);

  // Helper to robustly check if a unit is unclaimed
  const isUnclaimed = (unit: Unit) => {
    const claimers = unitClaims?.[unit.id];
    // unitClaims stores arrays of claimers
    if (Array.isArray(claimers)) {
      return claimers.length === 0;
    }
    // If no claim data exists, consider it unclaimed
    return true;
  };

  // Helper to check if a unit is a dispatch unit
  const isDispatchUnit = (unit: Unit) => 
    unit.type?.toUpperCase().includes('DISP') || unit.id === 'DISPATCH';

  // Memoized callback for checking if unit is unclaimed
  const isUnitUnclaimed = useCallback((u: Unit) => isUnclaimed(u), [unitClaims]);

  // Handle password change
  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await http.post('/auth/change-password', {
        oldPassword,
        newPassword
      });

      const data = response.data;
      if (data.success) {
        showSuccess('Password changed successfully');
        setShowPasswordDialog(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        showError(data.error || 'Failed to change password');
      }
    } catch (error) {
      showError('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Enhanced logout function that clears claimed unit, notifies Roblox, and closes radio windows
  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return; // Prevent multiple logout calls
    
    setIsLoggingOut(true);
    const ensureLocalLogout = async () => {
      await logout({ skipServerRequest: true });
    };
    
    try {
      try {
        await requestUnitUnclaim('logout');
      } catch (error) {
        console.warn('[Logout] requestUnitUnclaim failed, continuing anyway:', error);
      }

      try {
        sendUnitUnclaimBeacon('logout');
      } catch (error) {
        console.warn('[Logout] sendUnitUnclaimBeacon failed:', error);
      }

      // Close any open radio windows
      if ((window as any).radioAPI) {
        console.log('Closing radio window via Electron API');
        try {
          await (window as any).radioAPI.closeWindow();
        } catch (error) {
          console.error('Error closing radio window:', error);
        }
      }
      
      // Clear all intervals
      if (tokenValidationInterval.current) {
        clearInterval(tokenValidationInterval.current);
        tokenValidationInterval.current = null;
      }
      
      localStorage.removeItem('claimed_unit_id');
      localStorage.removeItem('cad_shift_data');

      try {
        await http.post('/auth/logout', {});
      } catch (error) {
        console.warn('[Logout] /auth/logout failed:', error);
      }

      await ensureLocalLogout();
    } catch (error) {
      console.error('Error during logout:', error);
      await ensureLocalLogout();
    } finally {
      window.location.reload();
    }
  }, [logout, isLoggingOut]);

  // Handle opening radio system (DISABLED - Beta feature)
  // const handleOpenRadio = useCallback(async () => {
  //   console.log('Radio button clicked');
  //   try {
  //     if ((window as any).radioAPI) {
  //       console.log('Using Electron Radio API');
  //       // Get current user from auth context
  //       const userId = user?.username || 'user2';
  //       const tokenData = await (window as any).radioAPI.requestToken(userId);
  //       await (window as any).radioAPI.openWindow(tokenData.token);
  //     } else {
  //       console.log('Opening radio in new window/tab');
  //       // In web browser, open radio in new window/tab
  //       const radioUrl = window.location.origin + '/radio?role=unit';
  //       
  //       // Try to open in new window first
  //       const radioWindow = window.open(radioUrl, '_blank', 'width=400,height=600,scrollbars=yes,resizable=yes');
  //       if (!radioWindow) {
  //         console.error('Failed to open radio window - popup blocked?');
  //         // Fallback: open in new tab
  //         const newTab = window.open(radioUrl, '_blank');
  //         if (!newTab) {
  //           showError('Failed to open radio window. Please allow popups or try opening manually.');
  //         } else {
  //           showSuccess('Radio system opened in new tab');
  //         }
  //       } else {
  //         showSuccess('Radio system opened in new window');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Failed to open radio:', error);
  //     showError('Failed to open radio system');
  //   }
  // }, [user, showSuccess, showError]);
  const [calls, setCalls] = useState<Call[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [currentUnit, setCurrentUnit] = useState<Unit | null>(null);
  const [isCallDetailsMaximized, setIsCallDetailsMaximized] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sortField, setSortField] = useState<string>('timeRcvdUnix');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // BigBoard modal state
  const [showBigBoard, setShowBigBoard] = useState(false);
  const [bigBoardData, setBigBoardData] = useState<any>(null);
  const unloadBeaconSentRef = useRef(false);

  // Load big board data when modal opens
  const loadBigBoardData = useCallback(async () => {
    try {
      const response = await http.get('/unit-assignments/big-board');
      if (response.data.success) {
        setBigBoardData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading big board data:', error);
    }
  }, []);

  // Load big board data when modal opens
  useEffect(() => {
    if (showBigBoard) {
      loadBigBoardData();
    }
  }, [showBigBoard, loadBigBoardData]);
  
  // Filter and grouping state with localStorage persistence
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>(() => {
    const saved = localStorage.getItem('unitInterface_selectedUnitTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    const saved = localStorage.getItem('unitInterface_selectedStatuses');
    return saved ? JSON.parse(saved) : [];
  });
  const [groupByType, setGroupByType] = useState<boolean>(() => {
    const saved = localStorage.getItem('unitInterface_groupByType');
    return saved ? JSON.parse(saved) : false;
  });
  const [groupByStatus, setGroupByStatus] = useState<boolean>(() => {
    const saved = localStorage.getItem('unitInterface_groupByStatus');
    return saved ? JSON.parse(saved) : false;
  });
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [unitSearchQuery, setUnitSearchQuery] = useState<string>(() => {
    const saved = localStorage.getItem('unitInterface_unitSearchQuery');
    return saved || '';
  });
  const [hideOOS, setHideOOS] = useState<boolean>(() => {
    const saved = localStorage.getItem('unitInterface_hideOOS');
    return saved ? JSON.parse(saved) : false;
  });
  const [columnWidths, setColumnWidths] = useState({
    id: 8,
    description: 30,
    location: 25,
    units: 20,
    box: 8,
    time: 8,
    status: 8
  });
  const [isResizing, setIsResizing] = useState<string | null>(null);

  // Save filter preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('unitInterface_selectedUnitTypes', JSON.stringify(selectedUnitTypes));
  }, [selectedUnitTypes]);

  useEffect(() => {
    localStorage.setItem('unitInterface_selectedStatuses', JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    localStorage.setItem('unitInterface_groupByType', JSON.stringify(groupByType));
  }, [groupByType]);

  useEffect(() => {
    localStorage.setItem('unitInterface_groupByStatus', JSON.stringify(groupByStatus));
  }, [groupByStatus]);

  useEffect(() => {
    localStorage.setItem('unitInterface_unitSearchQuery', unitSearchQuery);
  }, [unitSearchQuery]);

  useEffect(() => {
    localStorage.setItem('unitInterface_hideOOS', JSON.stringify(hideOOS));
  }, [hideOOS]);

  // Get call description from call code
  const getCallDescription = (callCode: string) => {
    const codeData = callCodes[callCode];
    return codeData ? codeData.desc.toUpperCase() : callCode.toUpperCase();
  };

  // Get unique unit types and statuses for filtering
  const getUniqueUnitTypes = () => {
    return Array.from(new Set(units.map(unit => unit.type).filter(Boolean)));
  };

  const getUniqueStatuses = () => {
    // Always return all possible statuses, regardless of current unit statuses
    return ['I/S', 'E/R', 'O/S', 'TRX', 'OOS', 'DISP'];
  };

  // Check if the current unit is attached to any call
  const isUnitAttachedToCall = () => {
    if (!currentUnit) return false;
    return calls.some(call => call.units && call.units[currentUnit.id]);
  };

  // Filter and group units
  const getFilteredAndGroupedUnits = () => {
    let filteredUnits = units.filter(unit => {
      const matchesSearch = unit.id.toLowerCase().includes(unitSearchQuery.toLowerCase());
      const matchesType = selectedUnitTypes.length === 0 || selectedUnitTypes.includes(unit.type || '');
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(unit.status || '');
      const matchesOOS = !hideOOS || unit.status !== 'OOS';
      
      return matchesSearch && matchesType && matchesStatus && matchesOOS;
    });

    // Sort units
    filteredUnits.sort((a, b) => {
      const typeOrder = {
        'ENG': 1, 'TRK': 2, 'ALS': 3, 'RESQ': 4, 'SUPV': 5, 'BLS': 6,
        'TNKR': 7, 'BRSH': 8, 'DIV': 9, 'HELI': 10, 'OTHER': 11, 'DISP': 12
      };
      
      const aTypeOrder = typeOrder[a.type as keyof typeof typeOrder] || 999;
      const bTypeOrder = typeOrder[b.type as keyof typeof typeOrder] || 999;
      
      if (aTypeOrder !== bTypeOrder) {
        return aTypeOrder - bTypeOrder;
      }
      
      return a.id.localeCompare(b.id);
    });

    // Group units if grouping is enabled
    if (groupByType && groupByStatus) {
      const groups: Record<string, Record<string, Unit[]>> = {};
      filteredUnits.forEach(unit => {
        const type = unit.type || 'OTHER';
        const status = unit.status || 'UNKNOWN';
        if (!groups[type]) groups[type] = {};
        if (!groups[type][status]) groups[type][status] = [];
        groups[type][status].push(unit);
      });
      return groups;
    } else if (groupByType) {
      const groups: Record<string, Unit[]> = {};
      filteredUnits.forEach(unit => {
        const type = unit.type || 'OTHER';
        if (!groups[type]) groups[type] = [];
        groups[type].push(unit);
      });
      return groups;
    } else if (groupByStatus) {
      const groups: Record<string, Unit[]> = {};
      filteredUnits.forEach(unit => {
        const status = unit.status || 'UNKNOWN';
        if (!groups[status]) groups[status] = [];
        groups[status].push(unit);
      });
      return groups;
    }

    return filteredUnits;
  };

  // Handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort calls based on current sort settings
  const getSortedCalls = (calls: Call[]) => {
    return [...calls].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'callCode':
          aValue = getCallDescription(a.callCode);
          bValue = getCallDescription(b.callCode);
          break;
        case 'location':
          aValue = a.location.toUpperCase();
          bValue = b.location.toUpperCase();
          break;
        case 'units':
          aValue = a.units ? Object.keys(a.units).length : 0;
          bValue = b.units ? Object.keys(b.units).length : 0;
          break;
        case 'boxArea':
          aValue = a.boxArea || '';
          bValue = b.boxArea || '';
          break;
        case 'timeRcvdUnix':
        default:
          aValue = a.timeRcvdUnix || 0;
          bValue = b.timeRcvdUnix || 0;
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle column resizing
  const handleMouseDown = (column: string, e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(column);
    
    const startX = e.clientX;
    const startWidth = columnWidths[column as keyof typeof columnWidths];
    
    const handleMouseMove = (e: MouseEvent) => {
      // Different constraints for different column types (in percentage)
      let minWidth = 5; // 5%
      let maxWidth = 20; // 20%
      
      if (['description', 'location', 'units'].includes(column)) {
        minWidth = 15; // 15%
        maxWidth = 40; // 40%
      } else if (['id', 'time', 'status', 'box'].includes(column)) {
        minWidth = 5; // 5%
        maxWidth = 15; // 15%
      }
      
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / window.innerWidth) * 100; // Convert pixels to percentage
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaPercent));
      
      setColumnWidths(prev => ({
        ...prev,
        [column]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Timer effect to update call timers every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);


  // Update claimed unit on server with role
  const updateClaimedUnitOnServer = useCallback(async (unitId: string, unitRole?: string) => {
    if (isLoggingOut) return;

    try {
      // Cookie-based authentication - credentials are sent automatically
      const response = await http.post('/auth/claim-unit', { 
        unitId,
        role: unitRole || 'driver' // Default to driver if no role specified
      });
      
      if (!response.data.success) {
        console.error('Failed to claim unit on server:', response.data.error);
        // Show user-friendly error from server
        const errorMessage = response.data.error || 'Failed to claim unit';
        showError(errorMessage);
        // Throw error so calling functions can handle the failure
        throw new Error(errorMessage);
      } else {
        console.log('[UnitInterface] Successfully claimed unit:', unitId, 'with role:', unitRole);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.log('Session invalid, logging out');
        handleLogout();
        throw error; // Re-throw to prevent optimistic updates from persisting
      } else if (error.response?.status === 403) {
        // Mode-based validation error (e.g., DISPATCH unit cannot be claimed)
        const errorMessage = error.response.data.error || 'You cannot claim this unit';
        showError(errorMessage);
        throw new Error(errorMessage); // Throw so calling functions can revert optimistic updates
      } else if (error.message) {
        // Re-throw if it's already an Error object (from the !response.data.success case)
        throw error;
      } else {
        console.error('Failed to claim unit:', error);
        const errorMessage = 'Failed to claim unit';
        showError(errorMessage);
        throw new Error(errorMessage);
      }
    }
  }, [handleLogout, isLoggingOut, showError]);

  // Helper function to update both claimed_unit_id and cad_shift_data when unit changes
  const updateUnitStorage = useCallback((unitId: string, unitRole?: string) => {
    localStorage.setItem('claimed_unit_id', unitId);
    if (unitRole) {
      localStorage.setItem('claimed_unit_role', unitRole);
    }
    
    // Also update cad_shift_data if it exists to keep them in sync
    const savedShiftData = localStorage.getItem('cad_shift_data');
    if (savedShiftData) {
      try {
        const shiftData = JSON.parse(savedShiftData);
        shiftData.unitId = unitId;
        if (unitRole) {
          shiftData.unitRole = unitRole;
        }
        localStorage.setItem('cad_shift_data', JSON.stringify(shiftData));
      } catch (error) {
        console.error('Error updating shift data:', error);
      }
    }
  }, []);

  // Check for user assignment in Active Dispatch mode
  const checkUserAssignment = useCallback(async () => {
    if (!isActiveDispatchMode || !user) return;
    
    try {
      const response = await http.get('/unit-assignments/big-board');
      if (response.data.success) {
        const assignments = response.data.data.assignments || {};
        // assignments is an object grouped by unit, so we need to search through all units
        let myAssignment = null;
        for (const unitId in assignments) {
          const unitAssignments = assignments[unitId];
          if (Array.isArray(unitAssignments)) {
            const found = unitAssignments.find((a: any) => a.userId === user?.id);
            if (found) {
              myAssignment = found;
              break;
            }
          }
        }
        
        setUserAssignment(myAssignment);
        
        if (myAssignment) {
          console.log('[UnitInterface] User has assignment to unit:', myAssignment.unitId);
        }
      }
    } catch (error) {
      console.error('[UnitInterface] Error checking user assignment:', error);
    }
  }, [isActiveDispatchMode, user]);

  // Manual claim handler for Active Dispatch mode
  // NOTE: This will be used by the "Claim Unit" button in the UI (see UNIT_INTERFACE_REMAINING_CHANGES.md)
  // @ts-ignore - Function will be used in UI implementation
  const handleClaimAssignedUnit = useCallback(async (unitRole?: string) => {
    if (!userAssignment) return;
    
    try {
      const unitId = userAssignment.unitId as string;
      
      // Optimistically update unitClaims before server call so handleStatusChange can immediately validate claim
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [unitId]: [...(prevClaims[unitId] || []), {
            id: user?.id,
            username: user?.username,
            robloxName: user?.robloxName,
            role: unitRole || 'driver'
          }]
        };
        console.log(`[UnitInterface] Optimistically updated unitClaims for ${unitId}: ${newClaims[unitId]?.length || 0} claimers`);
        return newClaims;
      });
      
      await updateClaimedUnitOnServer(unitId, unitRole);
      showSuccess(`Successfully claimed unit ${unitId}${unitRole ? ` as ${unitRole}` : ''}`);
      
      // Update local state - find the unit object and set it
      const assignedUnit = units.find(u => u.id === unitId);
      if (assignedUnit) {
        setCurrentUnit(assignedUnit);
        updateUnitStorage(unitId, unitRole);
      }
    } catch (error: any) {
      // Revert optimistic update on error
      const unitId = userAssignment.unitId as string;
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [unitId]: (prevClaims[unitId] || []).filter((claimer: any) => claimer.id !== user?.id)
        };
        console.log(`[UnitInterface] Reverted optimistic update for ${unitId}`);
        return newClaims;
      });
      showError(error.response?.data?.error || 'Failed to claim unit');
    }
  }, [userAssignment, updateClaimedUnitOnServer, showSuccess, showError, units, user, updateUnitStorage]);

  // Fetch all unit claims to update warning icons
  const refreshUnitClaims = useCallback(async () => {
    try {
      const claimsResponse = await http.get('/units/claims/all');
      if (claimsResponse.data?.success) {
        console.log('[UnitInterface] Refreshed unit claims:', claimsResponse.data.data);
        setUnitClaims(claimsResponse.data.data);
      }
    } catch (error) {
      console.error('[UnitInterface] Failed to refresh unit claims:', error);
    }
  }, []);

  // Load unit types from database
  const loadUnitTypes = useCallback(async () => {
    try {
      const response = await http.get('/unit-assignments/unit-types');
      if (response.data.success) {
        setUnitTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error loading unit types:', error);
    }
  }, []);

  // Check for assignment when in Active Dispatch mode
  useEffect(() => {
    if (isActiveDispatchMode && user) {
      checkUserAssignment();
    }
  }, [isActiveDispatchMode, user, checkUserAssignment]);

  // Load unit types on mount
  useEffect(() => {
    loadUnitTypes();
  }, [loadUnitTypes]);

  // Refresh unit claims periodically and on socket reconnection
  useEffect(() => {
    if (!socket) return;

    // Initial refresh when socket connects
    refreshUnitClaims();

    // Refresh on reconnection
    const handleReconnect = () => {
      console.log('[UnitInterface] Socket reconnected, refreshing unit claims');
      refreshUnitClaims();
    };

    socket.on('connect', handleReconnect);

    // Periodic refresh every 30 seconds as a backup to socket events
    const intervalId = setInterval(() => {
      refreshUnitClaims();
    }, 30000);

    return () => {
      socket.off('connect', handleReconnect);
      clearInterval(intervalId);
    };
  }, [socket, refreshUnitClaims]);

  // Get available unit roles from database based on unit type
  const getAvailableUnitRoles = useCallback((unitType: string): string[] => {
    if (!unitType || !unitTypes.length) {
      return []; // Return empty array if unitTypes not loaded yet
    }
    
    const unitTypeData = unitTypes.find(ut => ut.id === unitType?.toUpperCase());
    if (!unitTypeData) {
      return []; // Return empty array if unit type not found
    }
    
    // Combine required and optional roles
    return [...unitTypeData.requiredRoles, ...unitTypeData.optionalRoles];
  }, [unitTypes]);

  // Handle unit swap/reclaim with role
  const handleUnitSwap = useCallback(async (newUnitId: string, unitRole?: string) => {
    if (!newUnitId || newUnitId === currentUnit?.id) return;

    // Prevent claiming DISPATCH unit (only dispatchers can claim it)
    if (newUnitId === 'DISPATCH') {
      showError('DISPATCH unit cannot be claimed by field units.');
      return;
    }

    try {
      // If switching to a different unit, find an available role first
      if (newUnitId !== currentUnit?.id) {
        const newUnit = units.find(u => u.id === newUnitId);
        if (!newUnit) return;

        // Get available roles for the new unit
        const availableRoles = getAvailableUnitRoles(newUnit.type);
        const unitClaimers = unitClaims[newUnitId];
        
        if (availableRoles.length === 0) {
          showError(`Unit ${newUnitId} has no available roles`);
          return;
        }
        
        // Get unit type data to prefer first required role
        const unitTypeData = unitTypes.find(ut => ut.id === newUnit.type?.toUpperCase());
        const preferredRoles = unitTypeData 
          ? [...unitTypeData.requiredRoles, ...unitTypeData.optionalRoles]
          : availableRoles;
        
        // Find first available role (prefer first required role, then others)
        let selectedRole = preferredRoles[0] || availableRoles[0];
        
        const roleTaken = Array.isArray(unitClaimers) && 
          unitClaimers.some((claimer) => claimer.role === selectedRole);
        
        if (roleTaken) {
          // Find first available role
          const availableRole = preferredRoles.find(role => {
            const isRoleTaken = Array.isArray(unitClaimers) && 
              unitClaimers.some((claimer) => claimer.role === role);
            return !isRoleTaken;
          });
          
          if (availableRole) {
            selectedRole = availableRole;
          } else {
            showError(`Unit ${newUnitId} has no available roles`);
            return;
          }
        }

        // Update claimed unit on server with the selected role
        await updateClaimedUnitOnServer(newUnitId, selectedRole);
        
        // Update local state
        setCurrentUnit(newUnit);
        setCurrentUserRole(selectedRole);
        updateUnitStorage(newUnitId, selectedRole);
        showSuccess(`Switched to unit ${newUnitId} as ${selectedRole}`);
      } else if (unitRole) {
        // Staying on same unit but changing role
        await updateClaimedUnitOnServer(newUnitId, unitRole);
        setCurrentUserRole(unitRole);
        updateUnitStorage(newUnitId, unitRole);
        showSuccess(`Switched to unit ${newUnitId} as ${unitRole}`);
      }
    } catch (error) {
      console.error('Failed to swap unit:', error);
      showError('Failed to swap unit');
    }
  }, [currentUnit, units, updateClaimedUnitOnServer, showSuccess, showError, unitClaims, getAvailableUnitRoles, updateUnitStorage]);

  // Unit claims are now handled via real-time socket updates - no API calls needed

  // Token validation is handled by the useAuth hook and cookie-based authentication
  // No need for manual validation here as httpOnly cookies are automatically managed by the browser

  // Load initial data
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    
    const loadDataWithRetry = async () => {
      try {
        await loadData();
        retryCount = 0; // Reset retry count on success
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 2s, 4s, 8s
          console.log(`Retrying data load in ${delay}ms (attempt ${retryCount}/${maxRetries})`);
          setTimeout(loadDataWithRetry, delay);
        } else {
          console.error('Max retries reached for data loading');
        }
      }
    };
    
    loadDataWithRetry();
    
    // Handle window close event to unclaim without forcing logout (session remains valid)
    const handleBeforeUnload = () => {
      if (unloadBeaconSentRef.current) return;
      unloadBeaconSentRef.current = true;
      sendUnitUnclaimBeacon('window-unload');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return; // ignore bfcache restores
      handleBeforeUnload();
    };
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Restore claimed unit from localStorage when units are loaded
  useEffect(() => {
    if (isLoggingOut) return;
    
    // Prioritize claimed_unit_id as it represents the most recent user action (e.g., unit swap)
    // Only fall back to cad_shift_data if claimed_unit_id doesn't exist
    let unitToClaim = localStorage.getItem('claimed_unit_id');
    
    if (!unitToClaim) {
      // Fallback to shift data if no claimed unit ID exists
      const savedShiftData = localStorage.getItem('cad_shift_data');
      if (savedShiftData) {
        try {
          const shiftData = JSON.parse(savedShiftData);
          if (shiftData.unitId) {
            unitToClaim = shiftData.unitId;
          }
        } catch (error) {
          console.error('Error parsing shift data:', error);
        }
      }
    }
    
    if (unitToClaim && units.length > 0) {
      const unit = units.find(u => u.id === unitToClaim);
      if (unit) {
        setCurrentUnit(unit);
        
        // Set the current user role from the saved role or from unit claims
        const savedRole = localStorage.getItem('claimed_unit_role');
        const unitClaimers = unitClaims[unitToClaim];
        
        if (savedRole) {
          // Validate that the saved role is valid for this unit type
          const availableRoles = getAvailableUnitRoles(unit.type);
          if (availableRoles.includes(savedRole)) {
            setCurrentUserRole(savedRole);
            console.log('[UnitInterface] Set current user role from saved role:', savedRole);
          } else {
            console.warn('[UnitInterface] Invalid saved role:', savedRole, 'for unit type:', unit.type);
            console.warn('[UnitInterface] Available roles for', unit.type, ':', availableRoles);
            // Fallback to first available role if the saved role is invalid
            setCurrentUserRole(availableRoles[0] || '');
          }
        } else if (Array.isArray(unitClaimers)) {
          // Find the current user's role from unit claims
          const userClaim = unitClaimers.find((claimer) => claimer.id === user?.id);
          if (userClaim && userClaim.role) {
            // Validate that the role is a valid unit role (not a permission level)
            const availableRoles = getAvailableUnitRoles(unit.type);
            const permissionLevels = ['admin', 'dispatcher', 'unit', 'command'];
            
            // Skip if it's a permission level instead of a unit role
            if (permissionLevels.includes(userClaim.role.toLowerCase())) {
              console.warn('[UnitInterface] Skipping permission level as unit role:', userClaim.role);
              setCurrentUserRole(availableRoles[0] || ''); // Default fallback to first available role
            } else if (availableRoles.includes(userClaim.role)) {
              setCurrentUserRole(userClaim.role);
              console.log('[UnitInterface] Set current user role from unit claims:', userClaim.role);
            } else {
              console.warn('[UnitInterface] Invalid role from unit claims:', userClaim.role, 'for unit type:', unit.type);
              console.warn('[UnitInterface] Available roles for', unit.type, ':', availableRoles);
              // Fallback to first available role if the role is invalid
              setCurrentUserRole(availableRoles[0] || '');
            }
          }
        }
        
        // Check if the unit is already claimed by the current user
        const isAlreadyClaimed = Array.isArray(unitClaimers) && 
          unitClaimers.some((claimer) => claimer.id === user?.id);
        
        console.log('[UnitInterface] Claim check - Unit:', unitToClaim, 'User ID:', user?.id);
        console.log('[UnitInterface] Unit claimers:', unitClaimers);
        console.log('[UnitInterface] Already claimed by current user:', isAlreadyClaimed);
        
        if (!isAlreadyClaimed) {
          // Only claim the unit if it's not already claimed by the current user
          console.log('[UnitInterface] Attempting to claim unit:', unitToClaim, 'with role:', savedRole || 'undefined');
          updateClaimedUnitOnServer(unitToClaim, savedRole || undefined);
        } else {
          console.log('[UnitInterface] Unit', unitToClaim, 'already claimed by current user, skipping claim request');
        }
      }
    }
  }, [units.length, updateClaimedUnitOnServer, isLoggingOut]);

  // Disabled automatic role updates from server to prevent bouncing behavior
  // The role should only be set from localStorage or manual user selection
  /*
  useEffect(() => {
    if (currentUnit && user && Object.keys(unitClaims).length > 0) {
      const unitClaimers = unitClaims[currentUnit.id];
      if (Array.isArray(unitClaimers)) {
        const userClaim = unitClaimers.find((claimer) => claimer.id === user.id);
        if (userClaim && userClaim.role && userClaim.role !== currentUserRole) {
          // Validate that the role is a valid unit role (not a permission level)
          const availableRoles = getAvailableUnitRoles(currentUnit.type);
          const permissionLevels = ['admin', 'dispatcher', 'unit', 'command'];
          
          // Skip if it's a permission level instead of a unit role
          if (permissionLevels.includes(userClaim.role.toLowerCase())) {
            console.warn('[UnitInterface] Skipping permission level as unit role:', userClaim.role);
            // Don't update currentUserRole if it's a permission level
          } else if (availableRoles.includes(userClaim.role)) {
            setCurrentUserRole(userClaim.role);
            console.log('[UnitInterface] Updated current user role from unit claims:', userClaim.role);
          } else {
            console.warn('[UnitInterface] Invalid role from unit claims:', userClaim.role, 'for unit type:', currentUnit.type);
            console.warn('[UnitInterface] Available roles for', currentUnit.type, ':', availableRoles);
          }
        }
      }
    }
  }, [unitClaims, currentUnit, user, currentUserRole, getAvailableUnitRoles]);
  */

  // Periodic token validation removed - authentication is handled by httpOnly cookies
  // The server automatically validates on each request and the browser manages cookie expiration

  // Unit claims are now handled via real-time socket updates - no API calls needed

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (tokenValidationInterval.current) {
        clearInterval(tokenValidationInterval.current);
      }
    };
  }, []);

  // Use ref for version tracking to avoid stale closures
  const stateVersionRef = React.useRef<number>(0);
  
  // Track previous call IDs for audio notification on new calls
  const previousCallIdsRef = React.useRef<Set<string>>(new Set());
  
  // Track which call we've already auto-maximized for (to prevent re-maximizing on state updates)
  const autoMaximizedCallIdRef = React.useRef<string | null>(null);
  
  // Track last processed claim message for deduplication
  const lastProcessedClaimMessageRef = React.useRef<string>('');

  // State update handler with version monotonicity validation
  const handleStateUpdate = useCallback((data: any) => {
    const incomingVersion = data.version || 0;
    const currentVersion = stateVersionRef.current;
    
    // Version monotonicity validation - more lenient approach
    // Allow same version (idempotent updates) and newer versions
    if (incomingVersion < currentVersion && currentVersion > 0) {
      console.warn(`[UnitInterface State Update] Ignoring stale update: received version ${incomingVersion}, current version ${currentVersion}`);
      return;
    }

    // Log version gaps for debugging but don't block processing
    if (incomingVersion > currentVersion + 1 && currentVersion > 0) {
      console.warn(`[UnitInterface State Update] Version gap detected: expected ${currentVersion + 1}, received ${incomingVersion} (allowing anyway)`);
    }

    console.log(`[UnitInterface State Update] Processing authoritative state update version ${incomingVersion} (prev: ${currentVersion})`);
    
    // Update version ref immediately to prevent duplicate processing
    stateVersionRef.current = incomingVersion;
    
    // Update the last state update timestamp
    setLastStateUpdateTime(Date.now());

    // Batch state updates to avoid multiple re-renders
    if (data.calls) {
      const callsArray = Array.isArray(data.calls) ? data.calls : Object.values(data.calls);
      console.log(`[UnitInterface State Update] Received ${callsArray.length} active calls`);
      
      // Detect new calls for audio notification
      const currentCallIds = new Set<string>(callsArray.map((call: Call) => call.id));
      const previousCallIds = previousCallIdsRef.current;
      
      // Find new calls (exist in current but not in previous)
      const newCallIds = Array.from(currentCallIds).filter((id: string) => !previousCallIds.has(id));
      
      if (newCallIds.length > 0 && previousCallIds.size > 0) {
        // Play audio for new calls after initial load
        console.log(`[UnitInterface Audio] Detected ${newCallIds.length} new call(s):`, newCallIds);
        
        // Find the new calls to check if any are PENDING
        const newCalls = callsArray.filter((call: Call) => newCallIds.includes(call.id));
        const newPendingCalls = newCalls.filter((call: Call) => call.status === 'PENDING');
        
        // Play audio for new PENDING calls
        if (newPendingCalls.length > 0) {
          console.log(`[UnitInterface Audio] Found ${newPendingCalls.length} new PENDING call(s), playing new incident sound`);
          audioNotifications.playNewWaitingIncident();
        } else {
          console.log(`[UnitInterface Audio] New calls exist but none are PENDING, no audio played`);
        }
      } else if (newCallIds.length > 0) {
        // Initial load - play audio once if there are any PENDING calls
        const pendingCalls = callsArray.filter((call: Call) => call.status === 'PENDING');
        if (pendingCalls.length > 0) {
          console.log(`[UnitInterface Audio] Initial load with ${pendingCalls.length} PENDING call(s), playing new incident sound once`);
          audioNotifications.playNewWaitingIncident();
        } else {
          console.log(`[UnitInterface Audio] Initial load with ${newCallIds.length} call(s) but none are PENDING, no audio played`);
        }
      }
      
      // Update the previous call IDs for next comparison
      previousCallIdsRef.current = currentCallIds;
      
      // Always update calls when we get a new version - state-update is authoritative
      setCalls(callsArray);
      
      // Only update selected call if it exists and is still active
      setSelectedCall(prevSelected => {
        if (!prevSelected) return null;
        const updatedSelectedCall = callsArray.find((call: any) => 
          call.id === prevSelected.id && call.status !== 'CLOSED'
        );
        if (updatedSelectedCall) {
          return updatedSelectedCall;
        } else {
          // Call was closed or removed - clear selection and minimize
          console.log(`[UnitInterface State Update] Selected call ${prevSelected.id} is no longer active, clearing selection`);
          setIsCallDetailsMaximized(false);
          return null;
        }
      });
    }
    
    if (data.units) {
      const unitsArray = Array.isArray(data.units) ? data.units : Object.values(data.units);
      
      // Always update units when we get a new version - state-update is authoritative
      setUnits(unitsArray);
      
      // Update unitClaims state based on unit claimers
      // Note: Socket events provide detailed claimer arrays, state updates only have boolean flags
      // We preserve socket data and don't overwrite it with less detailed state data
      setUnitClaims(prevClaims => {
        const newUnitClaims: Record<string, any[]> = { ...prevClaims };
        unitsArray.forEach((unit: any) => {
          // If we don't have any claim data for this unit, initialize it
          if (!prevClaims[unit.id]) {
            newUnitClaims[unit.id] = [];
          }
          // Otherwise, keep the existing socket data (more detailed)
        });
        return newUnitClaims;
      });
      
      // Update currentUnit if it exists to keep navbar highlighting in sync
      setCurrentUnit(prevCurrentUnit => {
        if (!prevCurrentUnit) return null;
        const updatedCurrentUnit = unitsArray.find((unit: any) => unit.id === prevCurrentUnit.id);
        if (updatedCurrentUnit) {
          return updatedCurrentUnit;
        }
        return prevCurrentUnit;
      });
    }
    
    // Mark as ready after first successful state update
    setIsReady(prevReady => {
      if (!prevReady) {
        console.log(`[UnitInterface State Update] System ready with version ${incomingVersion}`);
        return true;
      }
      return prevReady;
    });

  }, []); // No dependencies - uses ref for version

  // Auto-select and maximize call when current unit gets assigned to it (only once per attachment)
  useEffect(() => {
    if (currentUnit && currentUnit.assignedCall) {
      const assignedCall = calls.find(call => call.id === currentUnit.assignedCall);
      if (assignedCall && assignedCall.status !== 'CLOSED') {
        // Only auto-maximize if we haven't already maximized for this call
        const shouldAutoMaximize = autoMaximizedCallIdRef.current !== assignedCall.id;
        
        if (shouldAutoMaximize) {
          setSelectedCall(assignedCall);
          setIsCallDetailsMaximized(true);
          autoMaximizedCallIdRef.current = assignedCall.id;
        }
        // Removed the else if block that was forcing selection back to assigned call
        // This allows users to view other calls even when attached to an incident
      } else if (assignedCall?.status === 'CLOSED' && autoMaximizedCallIdRef.current === assignedCall.id) {
        // Reset the ref when the call is closed
        autoMaximizedCallIdRef.current = null;
      }
    } else if (!currentUnit?.assignedCall && autoMaximizedCallIdRef.current) {
      // Reset the ref when unit is detached from all calls
      autoMaximizedCallIdRef.current = null;
    }
  }, [currentUnit, calls]);

  // Debounced state request function
  const requestStateDebounced = useCallback(() => {
    // Clear any existing debounce timer
    if (stateRequestDebounce) {
      clearTimeout(stateRequestDebounce);
    }
    
    // Set a new debounce timer
    const timer = setTimeout(async () => {
      const timeSinceLastUpdate = Date.now() - (lastStateUpdateTime || 0);
      const shouldResync = !isReady || timeSinceLastUpdate > 30000; // 30 seconds without updates
      
      if (shouldResync) {
        console.log('[UnitInterface Socket] Debounced state resync...');
        try {
          const response = await http.get('/state');
          if (response.data && response.data.success) {
            console.log(`[UnitInterface Socket] Resynced state version ${response.data.data.version}`);
            handleStateUpdate(response.data.data);
          }
        } catch (error) {
          console.error('[UnitInterface Socket] Failed to resync state:', error);
        }
      } else {
        console.log('[UnitInterface Socket] Recent state available, skipping debounced resync');
      }
    }, 2000); // 2 second debounce
    
    setStateRequestDebounce(timer);
  }, [isReady, lastStateUpdateTime, stateRequestDebounce, handleStateUpdate]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) {
      console.log('[UnitInterface] Socket not available yet, skipping event handlers');
      return;
    }

    // Handle socket reconnection and state resync
    const handleConnect = () => {
      console.log('[UnitInterface Socket] Connected');
      requestStateDebounced();
    };

    const handleDisconnect = () => {
      console.log('[UnitInterface Socket] Disconnected');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    const handleCallCreated = (newCall: Call) => {
      // Let state-update handle the actual state change to prevent version conflicts
      console.log(`[UnitInterface Socket] Call created event received:`, newCall);
      
      // Play audio notification for new pending calls
      if (newCall.status === 'PENDING') {
        console.log('[UnitInterface] Playing new waiting incident audio');
        audioNotifications.playNewWaitingIncident();
      }
    };

    const handleCallUpdated = (updatedCall: Call) => {
      // Let state-update handle the actual state change to prevent version conflicts
      console.log(`[UnitInterface Socket] Call updated event received:`, updatedCall);
      
      // Check if the user's claimed unit is attached to this call
      const claimedUnitId = currentUnit?.id || localStorage.getItem('claimed_unit_id');
      
      if (claimedUnitId && updatedCall.assignedUnits?.includes(claimedUnitId)) {
        console.log('[UnitInterface] Call updated for my unit, playing phone ring');
        audioNotifications.playPhoneRing();
      }
    };

    const handleUnitUpdated = (updatedUnit: Unit) => {
      // Let state-update handle the actual state change to prevent version conflicts
      console.log(`[UnitInterface Socket] Unit updated event received:`, updatedUnit);
    };

    const handleCommentAdded = (data: { callId: string; comment: any }) => {
      // Comments are now handled by authoritative state-update events
      // This handler is kept for backward compatibility but doesn't update state
      console.log(`[UnitInterface Comment] Comment added to call ${data.callId}, waiting for state-update`);
      
      // Check if the user's claimed unit is attached to this call
      const claimedUnitId = currentUnit?.id || localStorage.getItem('claimed_unit_id');
      
      // Find the call to check if user's unit is assigned
      const call = calls.find(c => c.id === data.callId);
      if (claimedUnitId && call?.assignedUnits?.includes(claimedUnitId)) {
        console.log('[UnitInterface] Comment added to my call, playing phone ring');
        audioNotifications.playPhoneRing();
      }
    };

    // Handle real-time unit claim updates from socket with deduplication
    const handleUnitClaimUpdated = (data: any) => {
      // Simple deduplication based on message content
      const messageKey = JSON.stringify(data);
      if (messageKey === lastProcessedClaimMessageRef.current) {
        console.log('[UnitInterface] Skipping duplicate unit claim update');
        return;
      }
      lastProcessedClaimMessageRef.current = messageKey;
      
      console.log('[UnitInterface] Received unit claim update:', data);
      
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [data.unitId]: data.claimedBy || []
        };
        console.log(`[UnitInterface] Updated unitClaims for ${data.unitId}: ${data.claimedBy?.length || 0} claimers. New state:`, newClaims);
        return newClaims;
      });
      
      // Check if the current user's unit claim was removed
      // Only clear if this unit is the current user's claimed unit AND the current user is no longer in claimedBy
      if (currentUnit?.id === data.unitId) {
        const currentUserId = user?.id;
        const claimedBy = data.claimedBy || [];
        const isUserStillClaimer = claimedBy.some((claimer: any) => claimer.id === currentUserId);
        
        // If the current user is not in the claimers list
        if (!isUserStillClaimer) {
          console.log('[UnitInterface] Current user\'s unit claim was removed, clearing currentUnit');
          
          // Clear the current unit and localStorage
          setCurrentUnit(null);
          localStorage.removeItem('claimed_unit_id');
          
          // Show notification to user
          showWarning(`Your claim to unit ${data.unitId} has been removed`);
        }
      }
      
      // Toast notifications are handled globally in App.tsx to prevent duplicates
    };

    // Handle user assignment changes
    const handleUserAssignmentChanged = (data: any) => {
      // Only react if this is for the current user
      if (data.userId !== user?.id) return;
      
      console.log('[UnitInterface] User assignment changed:', data);
      
      // Update currentUnit to the newly assigned unit
      const newUnit = units.find(u => u.id === data.unitId);
      if (newUnit) {
        setCurrentUnit(newUnit);
        // Toast notification is handled globally in App.tsx
      }
    };

    // Handle user assignment removal
    const handleUserAssignmentRemoved = (data: any) => {
      // Only react if this is for the current user
      if (data.userId !== user?.id) return;
      
      console.log('[UnitInterface] User assignment removed:', data);
      
      // Clear currentUnit
      setCurrentUnit(null);
      // Toast notification is handled globally in App.tsx
    };

    const handleUnitAssigned = (data: any) => {
      console.log('[UnitInterface] Unit assigned event received:', data);
      console.log('[UnitInterface] Current unit:', currentUnit);
      console.log('[UnitInterface] Current unit ID:', currentUnit?.id);
      console.log('[UnitInterface] Assigned unit ID:', data.unitId);
      console.log('[UnitInterface] User:', user);
      
      // Check if the assigned unit is the current user's claimed unit
      // Use currentUnit as primary check, fallback to localStorage
      const claimedUnitId = currentUnit?.id || localStorage.getItem('claimed_unit_id');
      console.log('[UnitInterface] Claimed unit ID:', claimedUnitId);
      console.log('[UnitInterface] Match?', claimedUnitId === data.unitId);
      
      if (claimedUnitId && data.unitId === claimedUnitId) {
        console.log('[UnitInterface] ✅ MATCH! Playing audio for unit assignment');
        audioNotifications.playCallAssignedToMe();
      } else {
        console.log('[UnitInterface] ❌ No match - unit not assigned to current user');
      }
      
      // State will arrive via 'state-update'
    };

    socket.on('call-created', handleCallCreated);
    socket.on('call-updated', handleCallUpdated);
    socket.on('unit-updated', handleUnitUpdated);
    socket.on('unit-assigned', handleUnitAssigned);
    socket.on('state-update', handleStateUpdate);
    socket.on('comment-added', handleCommentAdded);
    socket.on('unit-claim-updated', handleUnitClaimUpdated);
    socket.on('user-assignment-changed', handleUserAssignmentChanged);
    socket.on('user-assignment-removed', handleUserAssignmentRemoved);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('call-created', handleCallCreated);
      socket.off('call-updated', handleCallUpdated);
      socket.off('unit-updated', handleUnitUpdated);
      socket.off('unit-assigned', handleUnitAssigned);
      socket.off('state-update', handleStateUpdate);
      socket.off('comment-added', handleCommentAdded);
      socket.off('unit-claim-updated', handleUnitClaimUpdated);
      socket.off('user-assignment-changed', handleUserAssignmentChanged);
      socket.off('user-assignment-removed', handleUserAssignmentRemoved);
      
      // Cleanup debounce timer
      if (stateRequestDebounce) {
        clearTimeout(stateRequestDebounce);
      }
    };
  }, [socket, handleStateUpdate, showInfo, requestStateDebounced, currentUnit, units]);

  const loadData = async () => {
    // Prevent multiple simultaneous calls
    if (isDataLoading) {
      console.log('[UnitInterface] Data loading already in progress, skipping...');
      return;
    }
    
    try {
      setIsDataLoading(true);
      setLoading(true);
      
      // Use state endpoint for authoritative initial load
      const stateResponse = await http.get('/state');
      if (stateResponse.data && stateResponse.data.success && stateResponse.data.data) {
        console.log(`[UnitInterface Load Data] Loaded authoritative state version ${stateResponse.data.data.version}`);
        handleStateUpdate(stateResponse.data.data);
      } else {
        // Fallback to individual endpoints
        console.log('[UnitInterface Load Data] State endpoint unavailable, falling back to individual endpoints');
        const [callsResponse, unitsResponse] = await Promise.all([
          http.get('/calls'),
          http.get('/units')
        ]);
        
        // Handle API response format - extract data from success response
        const callsData = callsResponse.data?.success && callsResponse.data?.data ? callsResponse.data.data : (callsResponse.data || []);
        const unitsData = unitsResponse.data?.success && unitsResponse.data?.data ? unitsResponse.data.data : (unitsResponse.data || []);
        
        // Ensure we have arrays
        setCalls(Array.isArray(callsData) ? callsData : []);
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      }
      
      // Fetch current unit claims for all units
      try {
        const claimsResponse = await http.get('/units/claims/all');
        if (claimsResponse.data?.success) {
          console.log('[UnitInterface] Loaded unit claims:', claimsResponse.data.data);
          setUnitClaims(claimsResponse.data.data);
        }
      } catch (error) {
        console.error('[UnitInterface] Failed to load unit claims:', error);
        // Initialize with empty claims if fetch fails
        setUnitClaims({});
      }
    } catch (error) {
      console.error('[UnitInterface] Failed to load data:', error);
      setCalls([]);
      setUnits([]);
    } finally {
      setLoading(false);
      setIsDataLoading(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!currentUnit) {
      showWarning('Please select a unit first');
      return;
    }

    // Check if the user still has a valid claim to this unit
    const unitClaimers = unitClaims[currentUnit.id];
    const currentUserId = user?.id;
    const hasValidClaim = Array.isArray(unitClaimers) && 
      unitClaimers.some((claimer: any) => claimer.id === currentUserId);
    
    if (!hasValidClaim) {
      showWarning('You no longer have permission to change this unit\'s status');
      // Clear the current unit since the claim was removed
      setCurrentUnit(null);
      localStorage.removeItem('claimed_unit_id');
      return;
    }

    // Check if unit is trying to set E/R, O/S, or TRX without being attached to a call
    if (['E/R', 'O/S', 'TRX'].includes(status) && !isUnitAttachedToCall()) {
      showWarning('Unit must be attached to a call to set E/R, O/S, or TRX status');
      return;
    }

    // If unit is marking themselves I/S or OOS and they're on a call, check if they're the last unit
    if ((status === 'I/S' || status === 'OOS') && currentUnit.assignedCall) {
      // Find the call the unit is assigned to
      const assignedCall = calls.find(call => call.id === currentUnit.assignedCall);
      
      if (assignedCall) {
        const assignedUnitIds = Object.keys(assignedCall.units || {});
        const isLast = assignedUnitIds.length === 1 && assignedUnitIds.includes(currentUnit.id);
        
        if (isLast) {
          // Show confirmation dialog for last unit clearing
          setPendingStatusChange({ unitId: currentUnit.id, status, callId: assignedCall.id });
          setShowLastUnitPrompt(true);
          return; // Don't proceed with status change yet
        }
      }
      
      // Not the last unit, proceed with normal clearing
      try {
        const response = await http.post('/calls/assign', { 
          callId: currentUnit.assignedCall, 
          unitIds: [] // Empty array to remove all units
        });
        
        if (response.status !== 200) {
          console.error('Failed to remove unit from call:', response.statusText);
        }
      } catch (error) {
        console.error('Failed to remove unit from call:', error);
      }
    }

    // Optimistic update for immediate feedback
    setCurrentUnit(prev => prev ? { ...prev, status: status as Unit['status'] } : null);
    setUnits(prev => prev.map(unit => 
      unit.id === currentUnit.id ? { ...unit, status: status as Unit['status'] } : unit
    ));

    try {
      const response = await http.post('/units/status', { 
        unitId: currentUnit.id, 
        status 
      });
      
      if (response.status !== 200) {
        // Revert on error - the state-update will handle the correct state
        console.error('Failed to update unit status:', response.statusText);
      }
    } catch (error) {
      // Revert on error - the state-update will handle the correct state
      console.error('Failed to update unit status:', error);
    }
  };

  // Handle last unit clearing - set call to pending
  const handleLastUnitSetPending = useCallback(async () => {
    setShowLastUnitPrompt(false);
    if (!pendingStatusChange) return;
    
    try {
      // Update unit status
      await http.post('/units/status', { 
        unitId: pendingStatusChange.unitId, 
        status: pendingStatusChange.status 
      });
      
      // Set call to pending
      const res = await http.put(`/calls/${pendingStatusChange.callId}`, { status: 'PENDING' });
      if (res.status === 200) {
        showSuccess(`Unit cleared and call ${pendingStatusChange.callId} set back to pending`);
      }
      
      setPendingStatusChange(null);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to update call status';
      showError(msg);
      setPendingStatusChange(null);
    }
  }, [pendingStatusChange, showSuccess, showError]);

  // Handle last unit clearing - close call
  const handleLastUnitCloseCall = useCallback(async () => {
    setShowLastUnitPrompt(false);
    if (!pendingStatusChange) return;
    
    try {
      // Update unit status
      await http.post('/units/status', { 
        unitId: pendingStatusChange.unitId, 
        status: pendingStatusChange.status 
      });
      
      // Close the call
      const res = await http.post(`/calls/${pendingStatusChange.callId}/close`);
      if (res.status === 200) {
        showSuccess(`Unit cleared and call ${pendingStatusChange.callId} closed`);
      }
      
      setPendingStatusChange(null);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to close call';
      showError(msg);
      setPendingStatusChange(null);
    }
  }, [pendingStatusChange, showSuccess, showError]);

  // Handle unit row click - show unit roles popup
  const handleUnitClick = async (unit: Unit) => {
    // Prevent claiming DISPATCH unit (only dispatchers can claim it)
    if (unit.id === 'DISPATCH') {
      showError('DISPATCH unit cannot be claimed by field units.');
      return;
    }

    // Show unit roles dialog for any unit
    setSelectedUnitForRoles(unit);
    setShowUnitRolesDialog(true);
  };

  // Handle role selection and unit claiming
  const handleRoleSelection = async (unitRole: string) => {
    if (!selectedUnitForRole) return;

    // Prevent claiming DISPATCH unit (only dispatchers can claim it)
    if (selectedUnitForRole.id === 'DISPATCH') {
      showError('DISPATCH unit cannot be claimed by field units.');
      return;
    }

    try {
      // Optimistically update unitClaims before server call so handleStatusChange can immediately validate claim
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [selectedUnitForRole.id]: [...(prevClaims[selectedUnitForRole.id] || []), {
            id: user?.id,
            username: user?.username,
            robloxName: user?.robloxName,
            role: unitRole
          }]
        };
        console.log(`[UnitInterface] Optimistically updated unitClaims for ${selectedUnitForRole.id}: ${newClaims[selectedUnitForRole.id]?.length || 0} claimers`);
        return newClaims;
      });
      
      await updateClaimedUnitOnServer(selectedUnitForRole.id, unitRole);
      showSuccess(`Successfully claimed unit ${selectedUnitForRole.id} as ${unitRole}`);
      
      // Update local state
      setCurrentUnit(selectedUnitForRole);
      setCurrentUserRole(unitRole);
      updateUnitStorage(selectedUnitForRole.id, unitRole);
      
      // Close dialog
      setShowRoleSelectionDialog(false);
      setSelectedUnitForRole(null);
    } catch (error: any) {
      // Revert optimistic update on error
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [selectedUnitForRole.id]: (prevClaims[selectedUnitForRole.id] || []).filter((claimer: any) => claimer.id !== user?.id)
        };
        console.log(`[UnitInterface] Reverted optimistic update for ${selectedUnitForRole.id}`);
        return newClaims;
      });
      showError(error.response?.data?.error || 'Failed to claim unit');
    }
  };

  // Handle role change for current unit
  const handleRoleChange = async (newRole: string) => {
    if (!currentUnit) return;

    // Don't change if it's the same role
    if (newRole === currentUserRole) return;

    // Check if the role is already taken by another user
    const unitClaimers = unitClaims[currentUnit.id];
    if (Array.isArray(unitClaimers)) {
      const roleTaken = unitClaimers.some((claimer: any) => 
        claimer.role === newRole && claimer.id !== user?.id
      );
      
      if (roleTaken) {
        showError(`Role ${newRole} is already taken by another user`);
        return;
      }
    }

    try {
      // Optimistically update unitClaims to reflect role change before server call
      setUnitClaims(prevClaims => {
        const claimers = prevClaims[currentUnit.id] || [];
        const newClaimers = claimers.map((claimer: any) => 
          claimer.id === user?.id ? { ...claimer, role: newRole } : claimer
        );
        const newClaims = {
          ...prevClaims,
          [currentUnit.id]: newClaimers
        };
        console.log(`[UnitInterface] Optimistically updated role in unitClaims for ${currentUnit.id}`);
        return newClaims;
      });
      
      await updateClaimedUnitOnServer(currentUnit.id, newRole);
      showSuccess(`Successfully changed role to ${newRole}`);
      
      // Update local state
      setCurrentUserRole(newRole);
      updateUnitStorage(currentUnit.id, newRole);
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to change role');
    }
  };

  // Handle claiming a specific role from the roles popup
  const handleClaimRole = async (unit: Unit, role: string) => {
    // Prevent claiming DISPATCH unit (only dispatchers can claim it)
    if (unit.id === 'DISPATCH') {
      showError('DISPATCH unit cannot be claimed by field units.');
      return;
    }

    // Check if the role is already taken by another user
    const unitClaimers = unitClaims[unit.id];
    if (Array.isArray(unitClaimers)) {
      const roleTaken = unitClaimers.some((claimer: any) => 
        claimer.role === role && claimer.id !== user?.id
      );
      
      if (roleTaken) {
        showError(`Role ${role} is already taken by another user`);
        return;
      }
    }

    try {
      // Optimistically update unitClaims before server call so handleStatusChange can immediately validate claim
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [unit.id]: [...(prevClaims[unit.id] || []), {
            id: user?.id,
            username: user?.username,
            robloxName: user?.robloxName,
            role: role
          }]
        };
        console.log(`[UnitInterface] Optimistically updated unitClaims for ${unit.id}: ${newClaims[unit.id]?.length || 0} claimers`);
        return newClaims;
      });
      
      await updateClaimedUnitOnServer(unit.id, role);
      showSuccess(`Successfully claimed unit ${unit.id} as ${role}`);
      
      // Update local state
      setCurrentUnit(unit);
      setCurrentUserRole(role);
      updateUnitStorage(unit.id, role);
      
      // Close dialog
      setShowUnitRolesDialog(false);
      setSelectedUnitForRoles(null);
    } catch (error: any) {
      // Revert optimistic update on error
      setUnitClaims(prevClaims => {
        const newClaims = {
          ...prevClaims,
          [unit.id]: (prevClaims[unit.id] || []).filter((claimer: any) => claimer.id !== user?.id)
        };
        console.log(`[UnitInterface] Reverted optimistic update for ${unit.id}`);
        return newClaims;
      });
      showError(error.response?.data?.error || 'Failed to claim unit');
    }
  };

  // const handleSelfAttach = async (callId: string) => {
  //   if (!currentUnit) return;

  //   try {
  //     const response = await fetch('/api/calls/self-attach', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ 
  //         unitId: currentUnit.id, 
  //         callId: callId 
  //       })
  //     });
      
  //     if (response.ok) {
  //       loadData();
  //     }
  //   } catch (error) {
  //     console.error('Failed to self-attach to call:', error);
  //   }
  // };

  const getStatusColor = (status: string) => {
    // Using exact colors from server config.js unitColors
    switch (status) {
      case 'I/S': return '#32be5a'; // Color3.fromRGB(50,190,90)
      case 'DISP': return '#ffd25a'; // Color3.fromRGB(255,210,90)
      case 'E/R': return '#f0b432'; // Color3.fromRGB(240,180,50)
      case 'O/S': return '#3c96ff'; // Color3.fromRGB(60,150,255)
      case 'TRX': return '#c896ff'; // Color3.fromRGB(200,150,255)
      case 'TRX ARR': return '#b478f0'; // Color3.fromRGB(180,120,240)
      case 'OOS': return '#a0a0a0'; // Color3.fromRGB(160,160,160)
      case 'OOS-GAME': return '#787878'; // Color3.fromRGB(120,120,120)
      default: return '#a0a0a0';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }) + ' UTC';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show connection status if socket is not available
  if (!socket) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography sx={{ color: '#eceef1' }}>
          Connecting to CAD server...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', backgroundColor: '#101216', overflow: 'hidden' }}>
      {/* Header - matching DispatcherInterface */}
      <AppBar position="static" sx={{ backgroundColor: '#16181c', height: 60, flexShrink: 0 }}> {/* bgElev1 */}
        <Toolbar sx={{ minHeight: 60, height: 60, justifyContent: 'space-between' }}>
          {/* Left side: Icon, Title and Claimed Unit */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={iconImage}
              alt="Firestone CAD"
              onLoad={() => setIconLoaded(true)}
              onError={() => setIconLoaded(true)}
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                flexShrink: 0,
                opacity: iconLoaded ? 1 : 0.7,
                transition: 'opacity 0.2s ease-in-out'
              }}
            />
            
            {/* Show FIRESTONE CAD title only on larger screens */}
            {!isMobile && (
              <>
                <Typography variant="h6" sx={{ 
                  color: '#eceef1', 
                  fontSize: 24, 
                  fontWeight: 'bold' 
                }}>
                  FIRESTONE CAD
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: '#b4b9c0', 
                  fontSize: 24, 
                  fontWeight: 'bold' 
                }}>
                  |
                </Typography>
              </>
            )}
            {currentUnit ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Unit Selection Dropdown */}
                <FormControl sx={{ minWidth: 120 }}>
                  <Select
                    value={currentUnit.id}
                    onChange={(e) => handleUnitSwap(e.target.value as string)}
                    disabled={isActiveDispatchMode}
                    sx={{
                      backgroundColor: '#1a1d22',
                      color: '#eceef1',
                      fontWeight: 'bold',
                      height: 48,
                      // Trigger (selected value)
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#464a52' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
                      '& .MuiSelect-select': {
                        padding: isMobile ? '8px 12px' : '12px 16px',
                        fontSize: isMobile ? 16 : 24,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: isMobile ? 16 : 24,
                        fontWeight: 'bold',
                      },
                      '& .MuiSelect-icon': { color: '#eceef1' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#2c2f33',
                          color: '#eceef1',
                        },
                      },
                      MenuListProps: {
                        sx: {
                          py: 0,
                        },
                      },
                    }}
                  >
                    {units
                      .filter(u => {
                        // Always exclude DISPATCH unit
                        if (u.id === 'DISPATCH') return false;
                        
                        // In Active Dispatch mode, only show the assigned/current unit
                        if (isActiveDispatchMode && userAssignment) {
                          return u.id === userAssignment.unitId || u.id === currentUnit?.id;
                        }
                        
                        // In Self Dispatch mode, show all units
                        return true;
                      })
                      .sort((a, b) => {
                        const typeOrder = {
                          'ENG': 1, 'TRK': 2, 'ALS': 3, 'RESQ': 4, 'SUPV': 5, 'BLS': 6,
                          'TNKR': 7, 'BRSH': 8, 'DIV': 9, 'HELI': 10, 'OTHER': 11, 'DISP': 12
                        };
                        
                        const aTypeOrder = typeOrder[a.type as keyof typeof typeOrder] || 999;
                        const bTypeOrder = typeOrder[b.type as keyof typeof typeOrder] || 999;
                        
                        if (aTypeOrder !== bTypeOrder) {
                          return aTypeOrder - bTypeOrder;
                        }
                        
                        return a.id.localeCompare(b.id);
                      })
                      .map(unit => (
                        <MenuItem
                          key={unit.id}
                          value={unit.id}
                          sx={{
                            fontSize: 12,          // smaller font size
                            fontWeight: 'bold',
                            lineHeight: 1.2,
                            height: 32,            // smaller row height
                            minHeight: 32,
                            '&:hover': { backgroundColor: '#40444b' },
                            '&.Mui-selected': {
                              backgroundColor: '#dc2626',
                              '&:hover': { backgroundColor: '#dc2626' },
                            },
                          }}
                        >
                          {unit.id}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {/* Role Selection Dropdown */}
                <FormControl sx={{ minWidth: 100 }}>
                  <Select
                    value={currentUserRole || (currentUnit ? getAvailableUnitRoles(currentUnit.type)[0] : '')}
                    onChange={(e) => handleRoleChange(e.target.value as string)}
                    sx={{
                      backgroundColor: '#1a1d22',
                      color: '#eceef1',
                      fontWeight: 'bold',
                      height: 48,
                      // Trigger (selected value)
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#464a52' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
                      '& .MuiSelect-select': {
                        padding: isMobile ? '8px 12px' : '12px 16px',
                        fontSize: isMobile ? 14 : 18,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: isMobile ? 14 : 18,
                        fontWeight: 'bold',
                      },
                      '& .MuiSelect-icon': { color: '#eceef1' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#2c2f33',
                          color: '#eceef1',
                        },
                      },
                      MenuListProps: {
                        sx: {
                          py: 0,
                        },
                      },
                    }}
                  >
                    {getAvailableUnitRoles(currentUnit.type).map((role) => {
                      // Check if this role is taken by another user
                      const unitClaimers = unitClaims[currentUnit.id];
                      const isRoleTaken = Array.isArray(unitClaimers) && 
                        unitClaimers.some((claimer: any) => 
                          claimer.role === role && claimer.id !== user?.id
                        );
                      
                      return (
                        <MenuItem
                          key={role}
                          value={role}
                          disabled={isRoleTaken}
                          sx={{
                            fontSize: 12,
                            fontWeight: 'bold',
                            lineHeight: 1.2,
                            height: 32,
                            minHeight: 32,
                            '&:hover': { backgroundColor: '#40444b' },
                            '&.Mui-selected': {
                              backgroundColor: '#dc2626',
                              '&:hover': { backgroundColor: '#dc2626' },
                            },
                            '&.Mui-disabled': {
                              color: '#666',
                              opacity: 0.6,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 'bold' }}>
                              {role}
                            </Typography>
                            {isRoleTaken && (
                              <Typography sx={{ fontSize: 10, color: '#ff6b35', fontStyle: 'italic' }}>
                                (Taken)
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ 
                color: '#eceef1', 
                fontSize: isMobile ? 16 : 24, 
                fontWeight: 'bold' 
              }}>
                {currentUnit ? (currentUnit as Unit).id : 'NO UNIT CLAIMED'}
              </Typography>
            )}
          </Box>
          
          {/* Center: Status buttons - Desktop vs Mobile dropdown */}
          {!isMobile ? (
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flex: 1, flexWrap: 'nowrap' }}>
            <Button
              variant="contained"
              onClick={() => handleStatusChange('I/S')}
              disabled={!currentUnit}
              sx={{
                backgroundColor: `${getStatusColor('I/S')} !important`,
                color: '#0a0c0c', // chipText
                minWidth: 80,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                borderRadius: 3,
                border: currentUnit?.status === 'I/S' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#2aa54a !important' },
                '&:disabled': { backgroundColor: '#40444b !important', textShadow: 'none' }
              }}
            >
              I/S
            </Button>
            <Button
              variant="contained"
              onClick={() => handleStatusChange('E/R')}
              disabled={!currentUnit || !isUnitAttachedToCall()}
              sx={{
                backgroundColor: `${getStatusColor('E/R')} !important`,
                color: '#0a0c0c', // chipText
                minWidth: 80,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                borderRadius: 3,
                border: currentUnit?.status === 'E/R' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#d09a2a !important' },
                '&:disabled': { backgroundColor: '#40444b !important', textShadow: 'none' }
              }}
            >
              E/R
            </Button>
            <Button
              variant="contained"
              onClick={() => handleStatusChange('O/S')}
              disabled={!currentUnit || !isUnitAttachedToCall()}
              sx={{
                backgroundColor: `${getStatusColor('O/S')} !important`,
                color: '#0a0c0c', // chipText
                minWidth: 80,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                borderRadius: 3,
                border: currentUnit?.status === 'O/S' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#2c7ce6 !important' },
                '&:disabled': { backgroundColor: '#40444b !important', textShadow: 'none' }
              }}
            >
              O/S
            </Button>
            <Button
              variant="contained"
              onClick={() => handleStatusChange('TRX')}
              disabled={!currentUnit || !isUnitAttachedToCall()}
              sx={{
                backgroundColor: `${getStatusColor('TRX')} !important`,
                color: '#0a0c0c', // chipText
                minWidth: 80,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                borderRadius: 3,
                border: currentUnit?.status === 'TRX' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#b886ff !important' },
                '&:disabled': { backgroundColor: '#40444b !important', textShadow: 'none' }
              }}
            >
              TRX
            </Button>
            <Button
              variant="contained"
              onClick={() => handleStatusChange('OOS')}
              disabled={!currentUnit}
              sx={{
                backgroundColor: `${getStatusColor('OOS')} !important`,
                color: '#0a0c0c', // chipText
                minWidth: 80,
                height: 40,
                fontSize: 14,
                fontWeight: 'bold',
                borderRadius: 3,
                border: currentUnit?.status === 'OOS' ? '2px solid #ffffff' : '2px solid transparent',
                textShadow: '1px 1px 0px #ffffff, -1px -1px 0px #ffffff, 1px -1px 0px #ffffff, -1px 1px 0px #ffffff, 1px 1px 0px #000000, -1px -1px 0px #000000, 1px -1px 0px #000000, -1px 1px 0px #000000',
                '&:hover': { backgroundColor: '#909090 !important' },
                '&:disabled': { backgroundColor: '#40444b !important', textShadow: 'none' }
              }}
            >
              OOS
            </Button>
          </Box>
          ) : (
            /* Mobile: Status dropdown button */
            <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
              <Button
                variant="contained"
                onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                disabled={!currentUnit}
                sx={{
                  backgroundColor: currentUnit ? getStatusColor(currentUnit.status) : '#40444b',
                  color: '#0a0c0c',
                  minWidth: 120,
                  height: 40,
                  fontSize: 14,
                  fontWeight: 'bold',
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    backgroundColor: currentUnit ? getStatusColor(currentUnit.status) : '#40444b',
                    opacity: 0.8
                  },
                  '&:disabled': { 
                    backgroundColor: '#40444b !important', 
                    color: '#666 !important' 
                  }
                }}
              >
                {currentUnit ? currentUnit.status || 'SET STATUS' : 'NO UNIT'}
                <ArrowDropDownIcon />
              </Button>
              
              {/* Status Menu */}
              <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={() => setStatusMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    backgroundColor: '#2c2f33',
                    border: '1px solid #40444b',
                    minWidth: 150
                  }
                }}
              >
                <MenuList dense>
                  {[
                    { status: 'I/S', label: 'In Service' },
                    { status: 'E/R', label: 'En Route' },
                    { status: 'O/S', label: 'On Scene' },
                    { status: 'TRX', label: 'Transporting' },
                    { status: 'OOS', label: 'Out of Service' }
                  ].map(({ status, label }) => (
                    <MenuItem
                      key={status}
                      onClick={() => {
                        handleStatusChange(status);
                        setStatusMenuAnchor(null);
                      }}
                      disabled={!currentUnit || (status !== 'I/S' && status !== 'OOS' && !isUnitAttachedToCall())}
                      sx={{
                        color: '#eceef1',
                        fontSize: 14,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': { backgroundColor: '#40444b' },
                        '&.Mui-selected': { backgroundColor: '#3c8cff' },
                        '&.Mui-disabled': { color: '#666' }
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(status),
                          border: currentUnit?.status === status ? '2px solid #ffffff' : '2px solid transparent',
                        }}
                      />
                      <ListItemText primary={status} secondary={label} />
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
          )}
          
          {/* Right side: Clock, Return onReturnToShiftSelection and Connection Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            {/* Show time only on larger screens */}
            {!isMobile && (
              <Typography sx={{ 
                color: '#eceef1', 
                fontSize: 16, 
                fontWeight: 'bold' 
              }}>
                {formatTime(currentTime)}
              </Typography>
            )}
            
            {/* Return to Shift Selection button */}
            {onReturnToShiftSelection && (
              <IconButton 
                onClick={onReturnToShiftSelection} 
                sx={{ color: '#eceef1', padding: 0.5 }} 
                title="Return to Shift Selection"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: socket ? '#32be5a' : '#dc5050' 
            }} />
            
            {/* Desktop: Show all buttons */}
            {!isMobile && (
              <>
                <IconButton onClick={() => setShowBigBoard(true)} sx={{ color: '#eceef1', padding: 0.5 }} title="Open Big Board">
                  <BigBoardIcon fontSize="small" />
                </IconButton>
                {hasPermission('admin') && (
                  <IconButton onClick={() => setShowAdminDialog(true)} sx={{ color: '#eceef1', padding: 0.5 }} title="Admin Panel">
                    <AdminIcon fontSize="small" />
                  </IconButton>
                )}
                {hasPermission('shift-commander') && (
                  <IconButton onClick={() => setShowShiftCommander(true)} sx={{ color: '#eceef1', padding: 0.5 }} title="Shift Commander">
                    <PeopleIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton disabled sx={{ color: '#666', padding: 0.5 }} title="Radio System (Beta - Coming Soon)">
                  <RadioIcon fontSize="small" />
                </IconButton>
              </>
            )}
            
            {/* Essential buttons always visible */}
            <IconButton onClick={() => setShowAccountDialog(true)} sx={{ color: '#eceef1', padding: 0.5 }}>
              <AccountIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setLogoutDialog(true)} sx={{ color: '#eceef1', padding: 0.5 }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
            
            {/* Mobile: Add dropdown for additional actions */}
            {isMobile && (
              <>
                <IconButton 
                  onClick={(e) => setActionsMenuAnchor(e.currentTarget)} 
                  sx={{ color: '#eceef1', padding: 0.5 }}
                  title="More Actions"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                
                {/* Actions Menu */}
                <Menu
                  anchorEl={actionsMenuAnchor}
                  open={Boolean(actionsMenuAnchor)}
                  onClose={() => setActionsMenuAnchor(null)}
                  PaperProps={{
                    sx: {
                      backgroundColor: '#2c2f33',
                      border: '1px solid #40444b',
                      minWidth: 180
                    }
                  }}
                >
                  <MenuList dense>
                    <MenuItem
                      onClick={() => {
                        setShowBigBoard(true);
                        setActionsMenuAnchor(null);
                      }}
                      sx={{
                        color: '#eceef1',
                        fontSize: 14,
                        '&:hover': { backgroundColor: '#40444b' }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#eceef1', minWidth: 36 }}>
                        <BigBoardIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Big Board" />
                    </MenuItem>
                    
                    {hasPermission('admin') && (
                      <MenuItem
                        onClick={() => {
                          setShowAdminDialog(true);
                          setActionsMenuAnchor(null);
                        }}
                        sx={{
                          color: '#eceef1',
                          fontSize: 14,
                          '&:hover': { backgroundColor: '#40444b' }
                        }}
                      >
                        <ListItemIcon sx={{ color: '#eceef1', minWidth: 36 }}>
                          <AdminIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Admin Panel" />
                      </MenuItem>
                    )}
                    
                    {hasPermission('shift-commander') && (
                      <MenuItem
                        onClick={() => {
                          setShowShiftCommander(true);
                          setActionsMenuAnchor(null);
                        }}
                        sx={{
                          color: '#eceef1',
                          fontSize: 14,
                          '&:hover': { backgroundColor: '#40444b' }
                        }}
                      >
                        <ListItemIcon sx={{ color: '#eceef1', minWidth: 36 }}>
                          <PeopleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Shift Commander" />
                      </MenuItem>
                    )}
                    
                    <MenuItem
                      disabled
                      sx={{
                        color: '#666',
                        fontSize: 14
                      }}
                    >
                      <ListItemIcon sx={{ color: '#666', minWidth: 36 }}>
                        <RadioIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Radio System (Coming Soon)" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>


      {/* Main Content - responsive layout */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        padding: isMobile ? '8px' : '16px 16px 16px 0', 
        overflow: 'hidden', 
        minHeight: 0, 
        gap: isMobile ? '8px' : '16px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Left Panel - Units List */}
        <Paper
          sx={{
            width: isMobile ? '100%' : '20vw', // Full width on mobile, 20% on desktop
            minWidth: isMobile ? 'auto' : '250px',
            maxWidth: isMobile ? 'none' : '300px',
            height: isMobile ? (showMobileUnitsPanel ? '40vh' : '60px') : 'auto',
            backgroundColor: '#1c1f25', // surface
            border: '1px solid #40444b', // border
            borderRadius: 1.5,
            padding: isMobile ? 1 : 1.25,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'height 0.3s ease',
            ...(isMobile && {
              position: 'relative',
              zIndex: 1
            })
          }}
        >
          {/* Units Header */}
          <Box sx={{ marginBottom: isMobile ? 0 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? 0 : 1 }}>
              {/* Mobile toggle button */}
              {isMobile && (
                <IconButton
                  onClick={() => setShowMobileUnitsPanel(!showMobileUnitsPanel)}
                  sx={{ color: '#eceef1', marginRight: 1 }}
                  title="Toggle Units Panel"
                >
                  <MenuIcon />
                </IconButton>
              )}
              
              <Typography variant="h6" sx={{ 
                color: '#eceef1', 
                fontSize: isMobile ? 12 : 16, 
                fontWeight: 'bold', 
                flexGrow: 1 
              }}>
                UNITS
              </Typography>
              
              {/* Hide non-essential buttons on small mobile */}
              {!isSmallMobile && (
                <>
                  <IconButton
                    onClick={() => setShowFilterDialog(true)}
                    sx={{ color: '#eceef1', marginRight: 1 }}
                    title="Filter Units"
                  >
                    <FilterIcon />
                  </IconButton>
                  <Button
                    variant={hideOOS ? "contained" : "outlined"}
                    onClick={() => setHideOOS(!hideOOS)}
                    size="small"
                    sx={{
                      backgroundColor: hideOOS ? '#3c8cff' : 'transparent',
                      color: hideOOS ? '#0a0c0e' : '#eceef1',
                      borderColor: '#464a52',
                      fontSize: isMobile ? 9 : 11,
                      minWidth: isMobile ? 45 : 60,
                      height: isMobile ? 22 : 28,
                      marginRight: 1,
                      borderRadius: isMobile ? 1.5 : 2,
                      '&:hover': {
                        backgroundColor: hideOOS ? '#2a7ae6' : '#2a2a2a',
                        borderColor: '#3c8cff'
                      }
                    }}
                    title={hideOOS ? "Show OOS Units" : "Hide OOS Units"}
                  >
                    {isMobile ? 'OOS' : 'HIDE OOS'}
                  </Button>
                </>
              )}
              
              <IconButton
                sx={{ color: '#eceef1' }}
                title="Map View"
                size={isMobile ? 'small' : 'medium'}
                onClick={() => {
                  console.log('🗺️ UnitInterface: Map button clicked, opening map modal...');
                  setShowMapModal(true);
                }}
              >
                <MapIcon />
              </IconButton>
            </Box>

            {/* Search Box - only show when panel is open on mobile */}
            {(!isMobile || showMobileUnitsPanel) && (
              <TextField
                fullWidth
                size="small"
                placeholder="Search units..."
                value={unitSearchQuery}
                onChange={(e) => setUnitSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#eceef1' }} />
                    </InputAdornment>
                  ),
                  endAdornment: unitSearchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setUnitSearchQuery('')}
                        size="small"
                        sx={{ color: '#eceef1' }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  marginBottom: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1a1d22',
                    color: '#eceef1',
                    '& fieldset': {
                      borderColor: '#464a52',
                    },
                    '&:hover fieldset': {
                      borderColor: '#3c8cff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3c8cff',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#eceef1',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  },
                }}
              />
            )}
          </Box>

          {/* Units List - only show when panel is open on mobile */}
          {(!isMobile || showMobileUnitsPanel) && (
            <Box sx={{ 
              flex: 1, 
              overflowY: 'scroll',
              overflowX: 'hidden',
              minHeight: 0,
              paddingRight: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#1a1d22',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#464a52',
                borderRadius: '4px',
                '&:hover': {
                  background: '#5a5e66',
                },
              },
              // Always show vertical scrollbar, even when content doesn't overflow
              scrollbarWidth: 'thin',
              scrollbarColor: '#464a52 #1a1d22',
            }}>
            {(() => {
              const filteredAndGroupedUnits = getFilteredAndGroupedUnits();
              
              // Render grouped units
              if (groupByType || groupByStatus) {
                return Object.entries(filteredAndGroupedUnits as Record<string, any>).map(([groupKey, groupValue]) => (
                  <Box key={groupKey} sx={{ marginBottom: 1.5 }}>
                    {/* Group Header */}
                    <Typography sx={{ 
                      color: '#3c8cff', 
                      fontSize: 14, 
                      fontWeight: 'bold', 
                      marginBottom: 0.5,
                      paddingLeft: 1,
                      borderLeft: '3px solid #3c8cff'
                    }}>
                      {groupKey} ({Array.isArray(groupValue) ? groupValue.length : Object.values(groupValue).flat().length})
                    </Typography>
                    
                    {/* Group Units */}
                    {Array.isArray(groupValue) ? (
                      groupValue.map((unit: Unit) => (
                        <Box
                          key={unit.id}
                          sx={{
                            marginBottom: 0.75,
                            backgroundColor: getStatusColor(unit.status),
                            border: currentUnit?.id === unit.id ? '2px solid #3c8cff' : '1px solid #40444b',
                            borderRadius: 1,
                            padding: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 40,
                            cursor: 'pointer',
                            position: 'relative'
                          }}
                          onClick={() => handleUnitClick(unit)}
                        >
                          {/* Unit ID chip with warning icon */}
                          <Box sx={{ 
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {(() => {
                              const showWarn = isUnitUnclaimed(unit) && !isDispatchUnit(unit);
                              return (
                                <Tooltip title={showWarn ? 'Unit has no claimers' : ''} disableHoverListener={!showWarn}>
                                  <Chip
                                    icon={showWarn ? <WarningIcon /> : undefined}
                                    label={
                                      <Typography sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>
                                        {unit.id}
                                      </Typography>
                                    }
                                    size="small"
                                    sx={{
                                      backgroundColor: '#2a2d32',
                                      color: '#eceef1',
                                      height: 32,
                                      borderRadius: '4px',
                                      '& .MuiChip-label': { px: 1 },
                                      '& .MuiChip-icon': {
                                        color: '#ff6b35',
                                        fontSize: 18,
                                        mr: 0.5,
                                      },
                                    }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </Box>

                          {/* Status chip (right-aligned) */}
                          <Box sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }}>
                            <Chip
                              label={unit.status || '—'}
                              size="small"
                              sx={{
                                backgroundColor: '#2a2d32',
                                color: '#eceef1',
                                fontSize: 14,
                                fontWeight: 'bold',
                                height: 28,
                                borderRadius: '4px',
                                '& .MuiChip-label': {
                                  padding: '0 8px'
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      ))
                    ) : (
                      // Nested grouping (by type and status)
                      Object.entries(groupValue).map(([subGroupKey, subGroupUnits]) => (
                        <Box key={subGroupKey} sx={{ marginLeft: 2, marginBottom: 1 }}>
                          <Typography sx={{ 
                            color: '#eceef1', 
                            fontSize: 12, 
                            fontWeight: 'bold', 
                            marginBottom: 0.5,
                            paddingLeft: 1
                          }}>
                            {subGroupKey} ({(subGroupUnits as Unit[]).length})
                          </Typography>
                          {(subGroupUnits as Unit[]).map((unit: Unit) => (
                            <Box
                              key={unit.id}
                              sx={{
                                marginBottom: 0.75,
                                backgroundColor: getStatusColor(unit.status),
                                border: currentUnit?.id === unit.id ? '2px solid #3c8cff' : '1px solid #40444b',
                                borderRadius: 1,
                                padding: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 40,
                                cursor: 'pointer',
                                position: 'relative'
                              }}
                              onClick={() => handleUnitClick(unit)}
                            >
                              {/* Unit ID chip with warning icon */}
                              <Box sx={{ 
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                              {(() => {
                                const showWarn = isUnitUnclaimed(unit) && !isDispatchUnit(unit);
                                return (
                                  <Tooltip title={showWarn ? 'Unit has no claimers' : ''} disableHoverListener={!showWarn}>
                                    <Chip
                                      icon={showWarn ? <WarningIcon /> : undefined}
                                      label={
                                        <Typography sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>
                                          {unit.id}
                                        </Typography>
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: '#2a2d32',
                                        color: '#eceef1',
                                        height: 32,
                                        borderRadius: '4px',
                                        '& .MuiChip-label': { px: 1 },
                                        '& .MuiChip-icon': {
                                          color: '#ff6b35',
                                          fontSize: 18,
                                          mr: 0.5,
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })()}
                              </Box>

                              {/* Status chip (right-aligned) */}
                              <Box sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}>
                                <Chip
                                  label={unit.status || '—'}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#2a2d32',
                                    color: '#eceef1',
                                    fontSize: 14,
                                    fontWeight: 'bold',
                                    height: 28,
                                    borderRadius: '4px',
                                    '& .MuiChip-label': {
                                      padding: '0 8px'
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ))
                    )}
                  </Box>
                ));
              }
              
              // Render flat list (no grouping)
              return (filteredAndGroupedUnits as Unit[]).map((unit) => (
                <Box
                  key={unit.id}
                  sx={{
                    marginBottom: 0.75,
                    backgroundColor: getStatusColor(unit.status),
                    border: currentUnit?.id === unit.id ? '2px solid #3c8cff' : '1px solid #40444b',
                    borderRadius: 1,
                    padding: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 40,
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => handleUnitClick(unit)}
                >
                  {/* Unit ID chip with warning icon */}
                  <Box sx={{ 
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {(() => {
                      const showWarn = isUnitUnclaimed(unit) && !isDispatchUnit(unit);
                      return (
                        <Tooltip title={showWarn ? 'Unit has no claimers' : ''} disableHoverListener={!showWarn}>
                          <Chip
                            icon={showWarn ? <WarningIcon /> : undefined}
                            label={
                              <Typography sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>
                                {unit.id}
                              </Typography>
                            }
                            size="small"
                            sx={{
                              backgroundColor: '#2a2d32',
                              color: '#eceef1',
                              height: 32,
                              borderRadius: '4px',
                              '& .MuiChip-label': { px: 1 },
                              '& .MuiChip-icon': {
                                color: '#ff6b35',
                                fontSize: 18,
                                mr: 0.5,
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Box>

                  {/* Status chip (right-aligned) */}
                  <Box sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}>
                    <Chip
                      label={unit.status || '—'}
                      size="small"
                      sx={{
                        backgroundColor: '#2a2d32',
                        color: '#eceef1',
                        fontSize: 16,
                        fontWeight: 'bold',
                        height: 32,
                        borderRadius: '4px',
                        '& .MuiChip-label': {
                          padding: '0 8px'
                        }
                      }}
                    />
                  </Box>
                </Box>
              ));
            })()}
            </Box>
          )}
        </Paper>

        {/* Right Panel - Calls and Details */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* Calls Section - Responsive height */}
          <Paper
            sx={{
              height: isCallDetailsMaximized ? 0 : (isMobile ? '30vh' : '50%'), // Smaller height on mobile
              backgroundColor: '#1c1f25', // surface
              border: '1px solid #40444b', // border
              borderRadius: 1.5,
              padding: isCallDetailsMaximized ? 0 : (isMobile ? 1 : 1.25), // Smaller padding on mobile
              marginBottom: isCallDetailsMaximized ? 0 : (isMobile ? 1 : 1.25), // Smaller margin on mobile
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: isCallDetailsMaximized ? 0 : 1,
              transitionProperty: 'height, padding, margin, opacity',
              transitionDuration: '0.3s',
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {/* Calls Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <Typography variant="h6" sx={{ 
                color: '#eceef1', 
                fontSize: isMobile ? 12 : 16, 
                fontWeight: 'bold', 
                flexGrow: 1 
              }}>
                CALLS
              </Typography>
            </Box>

            {/* Calls Table */}
            <Box sx={{ 
            flex: 1, 
            overflowY: 'scroll',
            overflowX: 'hidden',
            minHeight: 0,
            paddingRight: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#1a1d22',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#464a52',
              borderRadius: '4px',
              '&:hover': {
                background: '#5a5e66',
              },
            },
            // Always show vertical scrollbar, even when content doesn't overflow
            scrollbarWidth: 'thin',
            scrollbarColor: '#464a52 #1a1d22',
          }}>
              {Array.isArray(calls) && calls.filter(call => call.status !== 'CLOSED').length > 0 ? (
                <Box sx={{
                  backgroundColor: '#1c1f25',
                  border: '1px solid #40444b',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '100%',
                  display: 'table',
                  tableLayout: 'fixed'
                }}>
                  {/* Table Header */}
                  <Box sx={{ 
                    display: 'flex',
                    backgroundColor: '#2a2d32',
                    borderBottom: '1px solid #40444b',
                    padding: '8px 0 8px 3px',
                    width: '100%'
                  }}>
                    <Box sx={{ 
                      width: `${columnWidths.id}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('id')}>
                        ID <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'id' ? '#3c8cff' : 'transparent',
                          zIndex: 2,
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e) => handleMouseDown('id', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.description}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('callCode')}>
                        DESCRIPTION <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'callCode' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'description' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e) => handleMouseDown('description', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.location}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('location')}>
                        LOCATION <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'location' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'location' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e) => handleMouseDown('location', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.units}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('units')}>
                        UNITS <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'units' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'units' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e) => handleMouseDown('units', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.box}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('boxArea')}>
                        BOX <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'boxArea' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'box' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e) => handleMouseDown('box', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.time}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('timeRcvdUnix')}>
                        TIME <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'timeRcvdUnix' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'time' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e) => handleMouseDown('time', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.status}%`,
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold' }}>STATUS</Typography>
                    </Box>
                  </Box>
                  
                  {/* Table Rows */}
                  {getSortedCalls(calls.filter(call => call.status !== 'CLOSED')).map((call) => {
                    const isAssigned = currentUnit && call.units && call.units[currentUnit.id];
                    const isSelected = selectedCall?.id === call.id;
                    
                    // Use reopenedAt if call was reopened, otherwise use timeRcvdUnix (created_at)
                    const baseTime = call.reopenedAt 
                      ? Math.floor(new Date(call.reopenedAt).getTime() / 1000)
                      : call.timeRcvdUnix || 0;
                    
                    const elapsedTime = baseTime ? 
                      Math.floor((currentTime.getTime() / 1000) - baseTime) : 0;
                    const minutes = Math.floor(elapsedTime / 60);
                    const seconds = elapsedTime % 60;
                    const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    
                    return (
                      <Box
                        key={call.id}
                        sx={{
                          display: 'flex',
                          padding: '8px 0 8px 3px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#2a2d32' : 'transparent',
                          borderLeft: isSelected ? '3px solid #3c8cff' : 
                                     isAssigned ? '3px solid #ff0000' : '3px solid transparent',
                          borderBottom: '1px solid #40444b',
                          '&:hover': { backgroundColor: '#2a2d32' }
                        }}
                        onClick={() => setSelectedCall(call)}
                      >
                        <Box sx={{ 
                          width: `${columnWidths.id}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.id}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.description}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {getCallDescription(call.callCode)}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.location}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.location.toUpperCase()}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.units}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.units ? Object.keys(call.units).join(', ') : '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.box}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.boxArea || '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.time}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {timeString}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.status}%`,
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Chip
                            label={call.status}
                            size="small"
                            sx={{
                              backgroundColor: call.status === 'PENDING' ? '#8b4513' : '#28502a',
                              color: call.status === 'PENDING' ? '#ffffff' : '#c8ffd2',
                              fontSize: 10,
                              fontWeight: 'bold',
                              height: 20,
                              width: 'fit-content'
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography sx={{ 
                  color: '#8c9198', 
                  textAlign: 'center', 
                  marginTop: 4, 
                  fontSize: isMobile ? 12 : 16 
                }}>
                  No active calls
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Call Details Section */}
          <Paper
            sx={{
              flex: 1, // Always take remaining space
              backgroundColor: '#1c1f25', // surface
              border: '1px solid #40444b', // border
              borderRadius: 1.5,
              padding: isMobile ? 1 : 1.25, // Smaller padding on mobile
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: isMobile ? '60vh' : 0 // Set minimum height on mobile
            }}
          >
            {selectedCall ? (
              <CallDetails 
                call={selectedCall} 
                units={units}
                selectedUnits={[]}
                onAttachUnits={async (unitIds) => {
                  // Self-dispatch: attach the claimed unit to the call
                  if (unitIds.length > 0 && currentUnit) {
                    try {
                      const response = await http.post('/calls/assign', {
                        callId: selectedCall.id,
                        unitIds: unitIds
                      });
                      
                      if (response.status === 200) {
                        showSuccess(`Unit ${currentUnit.id} self-dispatched to call ${selectedCall.id}`);
                      }
                    } catch (error: any) {
                      console.error('Failed to self-dispatch:', error);
                      const errorMessage = error.response?.data?.error || 'Failed to self-dispatch to call';
                      showError(errorMessage);
                    }
                  }
                }}
                onCommentsViewed={() => {
                  // Comments are now managed internally by CallDetails
                }}
                currentUserId={currentUnit?.id}
                currentUsername={user?.username}
                isMaximized={isCallDetailsMaximized}
                onToggleMaximize={() => setIsCallDetailsMaximized(!isCallDetailsMaximized)}
                onCallClosed={() => {
                  setIsCallDetailsMaximized(false);
                  setSelectedCall(null);
                }}
                showEditAndCloseButtons={units.some(unit => 
                  unit.type.toUpperCase().includes('DISP') && unit.status === 'OOS'
                )}
                allowSelfDispatch={units.some(unit => 
                  unit.type.toUpperCase().includes('DISP') && unit.status === 'OOS'
                )}
                allowSelfClose={units.some(unit => 
                  unit.type.toUpperCase().includes('DISP') && unit.status === 'OOS'
                )}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography sx={{ 
                  color: '#666', 
                  fontSize: isMobile ? 12 : 16 
                }}>
                  Select a call to view details
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Account Management Dialog */}
      <Dialog open={showAccountDialog} onClose={() => setShowAccountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Account Management</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Account Information</Typography>
            <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
              <strong>Username:</strong> {user?.username}
            </Typography>
            <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
              <strong>Roblox Name:</strong> {user?.robloxName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
              <strong>Role:</strong> {user?.role}
            </Typography>
            {user?.email && (
              <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
                <strong>Email:</strong> {user.email}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(true)} variant="outlined">
            Change Password
          </Button>
          <Button onClick={() => setShowAccountDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={passwordLoading}>
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#eceef1' }}>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>
            Are you sure you want to logout? This will close any open radio windows and clear your unit claim.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)} sx={{ color: '#b4b9c0' }}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setLogoutDialog(false);
              handleLogout();
            }} 
            variant="contained" 
            color="error"
            sx={{
              backgroundColor: '#dc5050',
              '&:hover': {
                backgroundColor: '#b84141'
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={showFilterDialog} onClose={() => setShowFilterDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: '#eceef1', backgroundColor: '#1a1d22', fontSize: 20, fontWeight: 'bold', padding: 3 }}>
          Filter Units
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1d22', padding: 4 }}>
          {/* Unit Type Filters */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold', marginBottom: 2 }}>
              Unit Types:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {getUniqueUnitTypes().map((type) => (
                <Chip
                  key={type}
                  label={type}
                  size="medium"
                  clickable
                  onClick={() => {
                    setSelectedUnitTypes(prev => 
                      prev.includes(type) 
                        ? prev.filter(t => t !== type)
                        : [...prev, type]
                    );
                  }}
                  sx={{
                    backgroundColor: selectedUnitTypes.includes(type) ? '#3c8cff' : '#464a52',
                    color: '#eceef1',
                    fontSize: 16,
                    fontWeight: 'bold',
                    height: 40,
                    padding: '0 12px',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: selectedUnitTypes.includes(type) ? '#2a5aa0' : '#5a5e66',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s ease'
                    },
                    '& .MuiChip-label': {
                      padding: '0 8px'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Status Filters */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold', marginBottom: 2 }}>
              Statuses:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {getUniqueStatuses().map((status) => {
                const isOOSDisabled = status === 'OOS' && hideOOS;
                return (
                  <Chip
                    key={status}
                    label={status}
                    size="medium"
                    clickable={!isOOSDisabled}
                    onClick={() => {
                      if (!isOOSDisabled) {
                        setSelectedStatuses(prev => 
                          prev.includes(status) 
                            ? prev.filter(s => s !== status)
                            : [...prev, status]
                        );
                      }
                    }}
                    sx={{
                      backgroundColor: selectedStatuses.includes(status) ? '#3c8cff' : '#464a52',
                      color: isOOSDisabled ? '#666' : '#eceef1',
                      fontSize: 16,
                      fontWeight: 'bold',
                      height: 40,
                      padding: '0 12px',
                      borderRadius: 2,
                      opacity: isOOSDisabled ? 0.5 : 1,
                      cursor: isOOSDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        backgroundColor: isOOSDisabled ? '#464a52' : (selectedStatuses.includes(status) ? '#2a5aa0' : '#5a5e66'),
                        transform: isOOSDisabled ? 'none' : 'scale(1.05)',
                        transition: 'all 0.2s ease'
                      },
                      '& .MuiChip-label': {
                        padding: '0 8px'
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Grouping Options */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography sx={{ color: '#eceef1', fontSize: 18, fontWeight: 'bold' }}>
              Grouping Options:
            </Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupByType}
                    onChange={(e) => setGroupByType(e.target.checked)}
                    sx={{ 
                      color: '#464a52', 
                      '&.Mui-checked': { color: '#3c8cff' },
                      transform: 'scale(1.2)'
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'medium', marginLeft: 1 }}>
                    Group by Type
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupByStatus}
                    onChange={(e) => setGroupByStatus(e.target.checked)}
                    sx={{ 
                      color: '#464a52', 
                      '&.Mui-checked': { color: '#3c8cff' },
                      transform: 'scale(1.2)'
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'medium', marginLeft: 1 }}>
                    Group by Status
                  </Typography>
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
          <Button 
            onClick={() => {
              setSelectedUnitTypes([]);
              setSelectedStatuses([]);
              setGroupByType(false);
              setGroupByStatus(false);
            }}
            sx={{ 
              color: '#b4b9c0',
              fontSize: 16,
              fontWeight: 'medium',
              padding: '8px 16px'
            }}
          >
            Clear All
          </Button>
          <Button 
            onClick={() => setShowFilterDialog(false)} 
            variant="contained"
            sx={{
              backgroundColor: '#3c8cff',
              fontSize: 16,
              fontWeight: 'bold',
              padding: '10px 24px',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#2a5aa0',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease'
              }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* BigBoard Modal */}
      <Dialog 
        open={showBigBoard} 
        onClose={() => setShowBigBoard(false)} 
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
            margin: '2.5vh auto',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#1976d2',
          color: 'white',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              BIG BOARD | {units.filter(u => u.status === 'I/S').length} UNITS IN SERVICE
            </Typography>
            {bigBoardData?.shift && (
              <Typography variant="body1" sx={{ ml: 2, opacity: 0.9 }}>
                Current Shift: {bigBoardData.shift.name} ({new Date(bigBoardData.shift.startTime).toLocaleDateString()} - {new Date(bigBoardData.shift.endTime).toLocaleDateString()})
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {new Date().toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short'
              })}
            </Typography>
            <IconButton onClick={() => setShowBigBoard(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(95vh - 64px)', overflow: 'hidden' }}>
          <BigBoard showHeader={false} />
        </DialogContent>
      </Dialog>

      {/* Admin Interface Dialog */}
      {hasPermission('admin') && (
        <AdminInterface 
          open={showAdminDialog} 
          onClose={() => setShowAdminDialog(false)} 
        />
      )}

      {/* Shift Commander Interface */}
      {hasPermission('shift-commander') && showShiftCommander && (
        <ShiftCommanderInterface
          onClose={() => setShowShiftCommander(false)}
        />
      )}

      {/* Map Modal */}
      <MapModal
        open={showMapModal}
        onClose={() => {
          console.log('🗺️ UnitInterface: MapModal onClose called');
          setShowMapModal(false);
        }}
      />

      {/* Role Selection Dialog */}
      <Dialog 
        open={showRoleSelectionDialog} 
        onClose={() => {
          setShowRoleSelectionDialog(false);
          setSelectedUnitForRole(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#eceef1', backgroundColor: '#1a1d22' }}>
          Select Role for Unit {selectedUnitForRole?.id}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>
            Choose your role for unit {selectedUnitForRole?.id} ({selectedUnitForRole?.type}):
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {selectedUnitForRole && getAvailableUnitRoles(selectedUnitForRole.type).map((role) => (
              <Button
                key={role}
                variant="outlined"
                onClick={() => handleRoleSelection(role)}
                sx={{
                  color: '#eceef1',
                  borderColor: '#464a52',
                  padding: '12px 16px',
                  fontSize: 16,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#3c8cff',
                    borderColor: '#3c8cff',
                    color: '#ffffff'
                  }
                }}
              >
                {role}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
          <Button 
            onClick={() => {
              setShowRoleSelectionDialog(false);
              setSelectedUnitForRole(null);
            }}
            sx={{ 
              color: '#b4b9c0',
              fontSize: 16,
              fontWeight: 'medium',
              padding: '8px 16px'
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Roles Dialog */}
      <Dialog 
        open={showUnitRolesDialog} 
        onClose={() => {
          setShowUnitRolesDialog(false);
          setSelectedUnitForRoles(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#eceef1', backgroundColor: '#1a1d22' }}>
          Unit {selectedUnitForRoles?.id} Roles ({selectedUnitForRoles?.type})
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
          <Typography sx={{ color: '#b4b9c0', mb: 3 }}>
            View and claim roles for unit {selectedUnitForRoles?.id}:
          </Typography>
          
          {selectedUnitForRoles && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {getAvailableUnitRoles(selectedUnitForRoles.type).map((role) => {
                // Read unitClaims directly to ensure we get the latest data
                const unitClaimers = unitClaims[selectedUnitForRoles.id] || [];
                const roleClaimer = Array.isArray(unitClaimers) 
                  ? unitClaimers.find((claimer: any) => claimer.role === role)
                  : null;
                const isCurrentUser = roleClaimer && roleClaimer.id === user?.id;
                const isRoleTaken = roleClaimer && !isCurrentUser;
                
                return (
                  <Box
                    key={role}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 2,
                      backgroundColor: isCurrentUser ? '#2a5aa0' : '#2a2d32',
                      border: isCurrentUser ? '2px solid #3c8cff' : '1px solid #40444b',
                      borderRadius: 2,
                      minHeight: 60
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ 
                        color: '#eceef1', 
                        fontSize: 18, 
                        fontWeight: 'bold',
                        minWidth: 100
                      }}>
                        {role}
                      </Typography>
                      
                      {roleClaimer ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ color: '#b4b9c0', fontSize: 14 }}>
                            Claimed by:
                          </Typography>
                          <Typography sx={{ 
                            color: isCurrentUser ? '#3c8cff' : '#eceef1', 
                            fontSize: 14, 
                            fontWeight: 'bold'
                          }}>
                            {roleClaimer.username}
                          </Typography>
                          {isCurrentUser && (
                            <Chip
                              label="You"
                              size="small"
                              sx={{
                                backgroundColor: '#3c8cff',
                                color: '#ffffff',
                                fontSize: 10,
                                height: 20
                              }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#666', fontSize: 14, fontStyle: 'italic' }}>
                          Available
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isCurrentUser ? (
                        <Chip
                          label="Your Role"
                          size="small"
                          sx={{
                            backgroundColor: '#3c8cff',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 'bold'
                          }}
                        />
                      ) : isRoleTaken ? (
                        <Chip
                          label="Taken"
                          size="small"
                          sx={{
                            backgroundColor: '#dc5050',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 'bold'
                          }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleClaimRole(selectedUnitForRoles, role)}
                          sx={{
                            backgroundColor: '#32be5a',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 'bold',
                            padding: '4px 12px',
                            '&:hover': {
                              backgroundColor: '#2aa54a'
                            }
                          }}
                        >
                          Claim
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
          <Button 
            onClick={() => {
              setShowUnitRolesDialog(false);
              setSelectedUnitForRoles(null);
            }}
            sx={{ 
              color: '#b4b9c0',
              fontSize: 16,
              fontWeight: 'medium',
              padding: '8px 16px'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Last Unit Clearing Confirmation Dialog */}
      <Dialog
        open={showLastUnitPrompt}
        onClose={() => {
          setShowLastUnitPrompt(false);
          setPendingStatusChange(null);
        }}
        PaperProps={{ sx: { backgroundColor: '#1c1f25', border: '1px solid #40444b' } }}
      >
        <DialogTitle sx={{ color: '#eceef1' }}>Last Unit Clearing from Call</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>
            You are the last unit on call {pendingStatusChange?.callId}.
          </Typography>
          <Typography sx={{ color: '#b4b9c0' }}>
            What would you like to do with this call?
          </Typography>
          {!units.some(unit => 
            unit.type.toUpperCase().includes('DISP') && unit.status === 'OOS'
          ) && (
            <Typography sx={{ color: '#ffa500', mt: 2, fontSize: '0.9em' }}>
              Note: Dispatcher is active. You can only set the call back to pending.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            onClick={() => {
              setShowLastUnitPrompt(false);
              setPendingStatusChange(null);
            }}
            sx={{ color: '#b4b9c0' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLastUnitSetPending}
            variant="contained"
            sx={{
              backgroundColor: '#ffa500',
              color: '#fff',
              '&:hover': { backgroundColor: '#ff8c00' }
            }}
          >
            Set to Pending
          </Button>
          {units.some(unit => 
            unit.type.toUpperCase().includes('DISP') && unit.status === 'OOS'
          ) && (
            <Button
              onClick={handleLastUnitCloseCall}
              variant="contained"
              sx={{
                backgroundColor: '#550000',
                color: '#fff',
                '&:hover': { backgroundColor: '#440000' }
              }}
            >
              Close Call
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnitInterface;